<div class="body">

    <div class="cover">
        <h1 style="border:none;font-size:30pt;">AVSS eSign / eStamp Platform</h1>
        <p style="font-size:16pt;color:#2E75B6;">Technical Documentation</p>
        <p style="font-size:11pt;color:#595959;">Admin Portal · Customer Portal · Maker/Checker Portal — Three Angular
            Applications</p>
        <p style="margin-top:60pt;font-size:10pt;color:#7F7F7F;">Generated from source code analysis · Repositories:
            <code>esign_frontend_angular</code> (Admin + Customer) and <code>estamp_frontend_angular</code>
            (Maker/Checker)<br />
            Document date: 27 August 2026
        </p>
    </div>

    <h2 id="toc">Table of Contents</h2>
    <div class="toc">
        <ol>
            <li><a href="#s1" (click)="scrollToSection($event, 's1')">Project Overview</a></li>
            <li><a href="#s2" (click)="scrollToSection($event, 's2')">Technology Stack</a></li>
            <li><a href="#s3" (click)="scrollToSection($event, 's3')">Project Structure</a></li>
            <li><a href="#s4" (click)="scrollToSection($event, 's4')">Application Architecture</a></li>
            <li><a href="#s5" (click)="scrollToSection($event, 's5')">Authentication and Authorization</a></li>
            <li><a href="#s6" (click)="scrollToSection($event, 's6')">Routing</a></li>
            <li><a href="#s7" (click)="scrollToSection($event, 's7')">Environment and Configuration</a></li>
            <li><a href="#s8" (click)="scrollToSection($event, 's8')">API Integration</a></li>
            <li><a href="#s9" (click)="scrollToSection($event, 's9')">Feature and Module Documentation</a></li>
            <li><a href="#s10" (click)="scrollToSection($event, 's10')">E-Sign Module</a></li>
            <li><a href="#s11" (click)="scrollToSection($event, 's11')">E-Stamp Module</a></li>
            <li><a href="#s12" (click)="scrollToSection($event, 's12')">Maker and Checker Modules</a></li>
            <li><a href="#s13" (click)="scrollToSection($event, 's13')">Orders and Inventory</a></li>
            <li><a href="#s14" (click)="scrollToSection($event, 's14')">Common and Reusable Components</a></li>
            <li><a href="#s15" (click)="scrollToSection($event, 's15')">Forms and Validation</a></li>
            <li><a href="#s16" (click)="scrollToSection($event, 's16')">HTTP Interceptors</a></li>
            <li><a href="#s17" (click)="scrollToSection($event, 's17')">Route Guards</a></li>
            <li><a href="#s18" (click)="scrollToSection($event, 's18')">Data Flow</a></li>
            <li><a href="#s19" (click)="scrollToSection($event, 's19')">State Management</a></li>
            <li><a href="#s20" (click)="scrollToSection($event, 's20')">File, PDF and OCR Functionality</a></li>
            <li><a href="#s21" (click)="scrollToSection($event, 's21')">Error Handling</a></li>
            <li><a href="#s22" (click)="scrollToSection($event, 's22')">Testing</a></li>
            <li><a href="#s23" (click)="scrollToSection($event, 's23')">Development Setup</a></li>
            <li><a href="#s24" (click)="scrollToSection($event, 's24')">Build and Deployment</a></li>
            <li><a href="#s25" (click)="scrollToSection($event, 's25')">Security</a></li>
            <li><a href="#s26" (click)="scrollToSection($event, 's26')">Business Rules</a></li>
            <li><a href="#s27" (click)="scrollToSection($event, 's27')">Dependencies</a></li>
            <li><a href="#s28" (click)="scrollToSection($event, 's28')">Troubleshooting</a></li>
            <li><a href="#s29" (click)="scrollToSection($event, 's29')">Coding Standards</a></li>
            <li><a href="#s30" (click)="scrollToSection($event, 's30')">Known Issues and Technical Debt</a></li>
            <li><a href="#s31" (click)="scrollToSection($event, 's31')">Future Improvements</a></li>
            <li><a href="#s32" (click)="scrollToSection($event, 's32')">Technical Glossary</a></li>
            <li><a href="#s33" (click)="scrollToSection($event, 's33')">Final Technical Summary</a></li>
        </ol>
    </div>

    <h2 id="s1">1. Project Overview</h2>

    <h3>1.1 Project Name</h3>
    <p>Two repositories together make up the platform's frontend:</p>
    <ul>
        <li><strong><code>esign_frontend_angular</code></strong> — contains the <strong>Admin</strong> and
            <strong>Customer</strong> Angular apps plus the shared Express host. Internally both Angular projects are
            still named <code>nazox</code> (original admin-template codename, visible in <code>angular.json</code>,
            <code>package.json</code>, and <code>app.component.ts</code>) — this has not been renamed even though the
            product is branded "AVSS eSign" / "AVSS eStamp" (per <code>Admin/README.md</code> "Avss Esign" and
            <code>Customer/README.md</code> "AVSS eSign"). The root Express host package is named
            <code>esign-estamp-web</code>.
        </li>
        <li><strong><code>estamp_frontend_angular</code></strong> — a separate repository, package name
            <code>e-stamp</code>, internally titled "EstampMaker" / "EstampMakerCheckerPanel" (per its own
            <code>README.md</code>). It hosts the <strong>Maker/Checker</strong> app used by AVSS's e-stamp fulfilment
            operations staff.
        </li>
    </ul>

    <h3>1.2 Project Purpose</h3>
    <p>This repository hosts a SaaS platform that provides two related document-compliance services to Indian
        businesses:</p>
    <ul>
        <li><strong>E-Sign</strong> — electronic document signing (Aadhaar OTP, DSC, and Aadhaar Biometric signing
            methods are selectable per signer in the send-document workflow).</li>
        <li><strong>E-Stamp</strong> — digital stamp-duty paper ordering, fulfilment and inventory management
            (bulk Excel-driven orders).</li>
    </ul>
    <p>The repository contains <strong>two independent Angular front-end applications</strong> — <strong>Admin</strong>
        (internal staff/operations console) and <strong>Customer</strong> (self-service tenant portal) — plus a shared
        Node/Express static-file host (<code>server.js</code>) that serves both from one Docker container, routed by
        HTTP <code>Host</code> header.</p>

    <h3>1.3 Business Objective</h3>
    <p>Based strictly on implemented functionality, the business objective is to let AVSS's customers (businesses)
        self-serve document e-signing and e-stamping, while AVSS staff (via the Admin app) manage tenant onboarding,
        subscription plans/billing, order fulfilment, and platform configuration.</p>

    <h3>1.4 Main Users and Roles</h3>
    <table>
        <tr>
            <th>Role</th>
            <th>Application</th>
            <th>Enforcement found in code</th>
        </tr>
        <tr>
            <td>Admin (platform staff)</td>
            <td>Admin app</td>
            <td><code>AuthGuard</code> requires <code>currentUser.role === 'admin'</code>
                (Admin/src/app/core/guards/auth.guard.ts)</td>
        </tr>
        <tr>
            <td>Customer (tenant/business user)</td>
            <td>Customer app</td>
            <td><code>AuthGuard</code> requires <code>currentUser.user.role === 'customer'</code>
                (Customer/src/app/core/guards/auth.guard.ts)</td>
        </tr>
        <tr>
            <td>Maker (e-stamp order submitter, per customer account)</td>
            <td>Referenced as data inside Admin (approval/assignment); <strong>acted on</strong> in the separate
                Maker/Checker app's <code>/maker/*</code> route tree</td>
            <td>Admin: approved/rejected via <code>/estamp/role</code>, assigned to a client via
                <code>client-user-mapping</code>. Maker/Checker app: <code>authGuard</code> requires
                <code>route.data.role === 'MAKER'</code> to match <code>currentUser.user.role</code>
                (<code>estamp_frontend_angular/src/app/core/guards/auth.guard.ts</code>)
            </td>
        </tr>
        <tr>
            <td>Checker</td>
            <td>Separate role in the Maker/Checker app's <code>/checker/*</code> route tree — <strong>a real role now
                    confirmed to exist</strong>, but its approval/remark actions are not reachable through the current
                UI (see §12)</td>
            <td><code>authGuard</code> requires <code>route.data.role === 'CHECKER'</code> to match
                <code>currentUser.user.role</code>
            </td>
        </tr>
    </table>
    <p class="note">The signer role in the E-Sign workflow (an external recipient asked to sign a document) is not an
        application user at all — signers authenticate on an external e-sign provider's hosted page reached via a
        <code>redirect_esign_url</code>, outside this codebase.
    </p>
    <p class="note">Role selection for the Maker/Checker app happens at <strong>registration</strong> (a radio choice,
        default <code>MAKER</code>) in <code>features/auth/register/register.component.ts</code> — there is no separate
        provisioning/invite flow.</p>

    <h3>1.5 Major Features (as implemented)</h3>
    <table>
        <tr>
            <th>Area</th>
            <th>Admin App</th>
            <th>Customer App</th>
            <th>Maker/Checker App</th>
        </tr>
        <tr>
            <td>Dashboard / analytics</td>
            <td>Platform-wide KPIs, revenue chart, document-status chart, access logs, Metabase-embedded BI iframes</td>
            <td>Per-tenant document/plan stats, e-sign &amp; e-stamp status dashboards</td>
            <td>Inventory &amp; order KPI cards, doughnut/bar charts by status/state/denomination (Chart.js)</td>
        </tr>
        <tr>
            <td>Customer/tenant management</td>
            <td>List, activate/deactivate, plan upgrade, order history</td>
            <td>Own profile, plan, branding, notifications</td>
            <td class="na">Not applicable</td>
        </tr>
        <tr>
            <td>E-Sign</td>
            <td>Document oversight (read-only), order &amp; template management, signature-position placement tool</td>
            <td>Full authoring: upload PDF, add signers, place signature fields, send, track, download, resend, audit
                trail</td>
            <td class="na">Not applicable — this app has no e-sign functionality</td>
        </tr>
        <tr>
            <td>E-Stamp</td>
            <td>Client order review/approval, invoice generation, payment verification, inventory oversight, maker
                approval, state master data</td>
            <td>Create single or bulk (Excel) stamp orders, upload payment proof, track status, print invoices,
                inventory listing</td>
            <td><strong>Maker:</strong> review bulk orders, check/allocate inventory, upload scanned stamp PDFs, fulfil
                or reject an order, approve/deface/delete individual inventory items. <strong>Checker:</strong>
                read-only dashboard + order list only (see §12 gap)</td>
        </tr>
        <tr>
            <td>Templates</td>
            <td>Reusable document templates with pre-placed signature positions</td>
            <td>Same, customer-owned templates</td>
            <td class="na">Not applicable</td>
        </tr>
        <tr>
            <td>Billing/Plans</td>
            <td>Plan CRUD, order completion/cancellation</td>
            <td>Subscription view, plan upgrade (COD only), order history, invoice print</td>
            <td class="na">Not applicable</td>
        </tr>
        <tr>
            <td>Integrations</td>
            <td>Vendor management (health-check), notification templates (email/SMS/WhatsApp)</td>
            <td>API key management, webhook config, API documentation/cURL/Postman export, S3 document storage
                integration</td>
            <td>A standalone PDF text-extraction tool that calls an external OCR-capable API (currently hidden from the
                sidebar) — unrelated to the core order workflow</td>
        </tr>
        <tr>
            <td>Platform config</td>
            <td>Settings (platform/security/compliance), cron jobs</td>
            <td class="na">Not applicable</td>
            <td>Own profile + change-password only</td>
        </tr>
    </table>

    <h3>1.6 High-Level Application Workflow</h3>
    <p><strong>Customer onboarding →</strong> a business registers via the Customer app's signup flow (email/password +
        OTP verification) → logs in → lands on a dashboard gated behind <code>AuthGuard</code>.<br />
        <strong>E-Sign flow:</strong> customer uploads a PDF (or picks a template) → adds recipients with a signing
        method (Aadhaar OTP / DSC / Aadhaar Biometric) → places signature boxes on the PDF via a canvas-based signer
        tool → submits → each recipient signs via an externally hosted redirect URL → customer tracks progress and
        downloads the completed document and its audit trail.<br />
        <strong>E-Stamp flow:</strong> customer creates a single order (uploads a document + stamp metadata) or a bulk
        order (uploads a validated Excel sheet of state/denomination/quantity rows) → Admin reviews the order, generates
        an invoice → customer uploads payment proof → Admin verifies or rejects the payment → order becomes actionable
        in the Maker/Checker app, where a Maker checks inventory availability, allocates or fulfils the order (or
        rejects it with a reason), and separately uploads scanned stamp PDFs that become new inventory items → a Checker
        is intended to perform a second-level review (implied by the <code>UNDER_CHECKER_REVIEW</code> status and
        dedicated backend endpoints) but this step is not currently reachable through the Checker app's UI.<br />
        <strong>Admin oversight:</strong> Admin staff manage tenants, plans, vendors, notification templates, and
        platform settings, and specifically for e-stamp: approve/reject maker users, assign makers to client accounts,
        and progress orders through the payment/invoice lifecycle.<br />
        <strong>Maker/Checker oversight:</strong> once assigned, a Maker user logs into the separate Maker/Checker app
        to process the physical/e-stamp fulfilment side of an order — this app talks to a different backend host than
        Admin/Customer (see §7.1) but operates on the same order record, linked by the shared <code>bsrId</code> field
        and status vocabulary.
    </p>

    <h2 id="s2">2. Technology Stack</h2>

    <table>
        <tr>
            <th>Technology</th>
            <th>Admin App Version</th>
            <th>Customer App Version</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td>Angular (core/cdk/forms/router/animations/localize)</td>
            <td>^17.0.6</td>
            <td>^19.2.25</td>
            <td>SPA framework. <strong>The two apps run on different major Angular versions</strong> — they are fully
                independent builds, not a shared workspace.</td>
        </tr>
        <tr>
            <td>TypeScript</td>
            <td>~5.2.2</td>
            <td>~5.7.3</td>
            <td>Static typing (no <code>strict: true</code> in either app's <code>tsconfig.json</code> — loosely typed
                codebase, extensive use of <code>any</code>)</td>
        </tr>
        <tr>
            <td>Node.js</td>
            <td colspan="2">&gt;=20.11.0 (root <code>package.json engines</code>); Docker base image
                <code>node:20-alpine</code>
            </td>
            <td>Runtime for the Angular CLI build and the Express host</td>
        </tr>
        <tr>
            <td>npm</td>
            <td colspan="2">Bundled with Node 20</td>
            <td>Package manager; Docker build uses <code>npm ci --legacy-peer-deps</code> due to peer-dependency
                conflicts (see §27)</td>
        </tr>
        <tr>
            <td>Angular Material</td>
            <td class="na">Not used</td>
            <td class="na">Not used</td>
            <td>—</td>
        </tr>
        <tr>
            <td>Bootstrap</td>
            <td>5.3.2</td>
            <td>5.3.2</td>
            <td>Primary CSS/layout framework for both apps (SCSS imported in <code>angular.json</code> styles)</td>
        </tr>
        <tr>
            <td>PrimeNG / PrimeIcons</td>
            <td>^17.18.15 / ^7.0.0</td>
            <td>^17.18.15 / ^7.0.0</td>
            <td>Data tables (<code>p-table</code> with server-side lazy loading/pagination), dropdowns, calendars — the
                primary UI kit for all list/filter screens</td>
        </tr>
        <tr>
            <td>Tailwind CSS</td>
            <td class="na">Not present</td>
            <td>^3.4.19 (devDependency)</td>
            <td>Present in Customer app's <code>tailwind.config.js</code> but minimally configured (only content globs,
                no theme/plugins) — Bootstrap remains the dominant styling system</td>
        </tr>
        <tr>
            <td>&#64;ng-bootstrap/ng-bootstrap</td>
            <td>^16.0.0</td>
            <td>^18.0.0</td>
            <td>Bootstrap-flavoured Angular components (modals, alerts, dropdowns)</td>
        </tr>
        <tr>
            <td>RxJS</td>
            <td>~7.8.1</td>
            <td>~7.8.1</td>
            <td>Reactive HTTP calls, <code>BehaviorSubject</code>/<code>Subject</code>-based state (toast,
                confirm-modal, auth session, event bus)</td>
        </tr>
        <tr>
            <td>pdfjs-dist</td>
            <td>^3.11.174</td>
            <td>^3.11.174</td>
            <td>Client-side PDF rendering to <code>&lt;canvas&gt;</code> for the signature-placement tool and PDF
                preview</td>
        </tr>
        <tr>
            <td>pdf-lib</td>
            <td>^1.17.1</td>
            <td>^1.17.1</td>
            <td>Programmatically draws signature-placeholder rectangles/labels onto a copy of the PDF for preview
                generation</td>
        </tr>
        <tr>
            <td>html2pdf.js</td>
            <td class="na">Not present</td>
            <td>^0.14.0</td>
            <td>DOM-to-PDF export, used only by the (currently disabled) audit-trail PDF download and the
                API-documentation PDF export</td>
        </tr>
        <tr>
            <td>xlsx</td>
            <td class="na">Not present</td>
            <td>^0.18.5</td>
            <td>Client-side Excel parsing/validation for bulk e-stamp order upload</td>
        </tr>
        <tr>
            <td>&#64;ckeditor/ckeditor5-build-classic</td>
            <td>^40.2.0</td>
            <td>^40.2.0</td>
            <td>Rich-text editor — Admin's notification-template email-body editor</td>
        </tr>
        <tr>
            <td>ngx-mask</td>
            <td>^17.0.4</td>
            <td>^17.0.4</td>
            <td>Input masking</td>
        </tr>
        <tr>
            <td>ngx-cookie-service</td>
            <td>^17.0.1</td>
            <td>^19.1.2</td>
            <td>Cookie access (used only for the <code>lang</code> language-preference cookie)</td>
        </tr>
        <tr>
            <td>&#64;ngx-translate/core + http-loader</td>
            <td>^15.0.0 / ^8.0.0</td>
            <td>^15.0.0 / ^8.0.0</td>
            <td>i18n framework, loads <code>assets/i18n/*.json</code> — supported languages en/es/de/it/ru per the
                topbar language switcher</td>
        </tr>
        <tr>
            <td>sweetalert2</td>
            <td>^11.10.1</td>
            <td>^11.10.1</td>
            <td>Styled alert dialogs (legacy template dependency; app also has its own custom
                <code>ConfirmModalService</code>/<code>app-confirm-modal</code>)
            </td>
        </tr>
        <tr>
            <td>ApexCharts / ng-apexcharts</td>
            <td>^3.44.2 / ^1.8.0</td>
            <td>^3.44.2 / ^1.8.0</td>
            <td>Dashboard charts (revenue trend, document-status donut)</td>
        </tr>
        <tr>
            <td>firebase</td>
            <td>^10.7.1</td>
            <td>^10.7.1</td>
            <td>Legacy/optional auth backend (<code>authUtils.ts</code>) — only active when
                <code>environment.defaultauth === 'firebase'</code>; both apps are actually configured with
                <code>'local'</code> (Admin) / <code>'fackbackend'</code> (Customer), so Firebase auth is present but
                dormant
            </td>
        </tr>
        <tr>
            <td>&#64;ng-select/ng-select</td>
            <td>^12.0.4</td>
            <td>^14.9.0</td>
            <td>Searchable select dropdowns</td>
        </tr>
        <tr>
            <td>angular-ng-stepper / &#64;angular/cdk stepper</td>
            <td>^2.0.0</td>
            <td>^2.0.0</td>
            <td>Multi-step wizard — Customer's Send-Document flow</td>
        </tr>
        <tr>
            <td>&#64;asymmetrik/ngx-leaflet + leaflet</td>
            <td>^17.0.0 / ^1.9.4</td>
            <td>^18.0.0 / ^1.9.4</td>
            <td>Map rendering (present as a dependency; no specific usage was confirmed within the analyzed feature
                scope)</td>
        </tr>
        <tr>
            <td>&#64;avss-tech/logger</td>
            <td>^1.0.7</td>
            <td class="na">Not present</td>
            <td>Internal AVSS logging/access-log SDK, wraps the Admin access-logs and dashboard "recent access logs"
                widget</td>
        </tr>
        <tr>
            <td>Karma + Jasmine</td>
            <td>^6.4.2 / ~5.1.1</td>
            <td>^6.4.2 / ~5.1.1</td>
            <td>Unit test runner/framework (see §22 — coverage is nominal only)</td>
        </tr>
        <tr>
            <td>Protractor</td>
            <td>^7.0.0</td>
            <td>^7.0.0</td>
            <td>E2E test runner wired via the <code>e2e</code> npm script; Protractor itself has been
                end-of-life/deprecated upstream since 2023</td>
        </tr>
        <tr>
            <td>ESLint / TSLint (both present)</td>
            <td>&#64;angular-eslint ^17 + tslint ~6.1.3</td>
            <td>&#64;angular-eslint ^19 + tslint ~6.1.3</td>
            <td>Two overlapping lint toolchains coexist in both apps (TSLint is deprecated upstream)</td>
        </tr>
    </table>

    <h4>2.1 Root Express Host (not an Angular dependency)</h4>
    <table>
        <tr>
            <th>Package</th>
            <th>Version</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td>express</td>
            <td>^4.19.2</td>
            <td>Static file server + SPA fallback for both built Angular apps</td>
        </tr>
        <tr>
            <td>helmet</td>
            <td>^7.1.0</td>
            <td>HTTP security headers (CSP explicitly disabled — see §25)</td>
        </tr>
        <tr>
            <td>compression</td>
            <td>^1.7.4</td>
            <td>Gzip response compression</td>
        </tr>
    </table>

    <h4>2.2 Maker/Checker App (separate repository, <code>estamp_frontend_angular</code>)</h4>
    <p>This app is architecturally distinct from Admin/Customer — it uses Angular's newer <strong>standalone
            components</strong> model (no NgModules for feature components) and a different major Angular version.</p>
    <table>
        <tr>
            <th>Technology</th>
            <th>Version</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td>Angular (core/cdk/forms/router/animations)</td>
            <td>^21.2.19</td>
            <td>SPA framework — standalone-components architecture, newest Angular major version of all three apps</td>
        </tr>
        <tr>
            <td>Angular Material</td>
            <td>^21.2.14</td>
            <td><strong>The only app of the three that uses Angular Material</strong> (dialogs, snackbar, progress
                spinner, sidenav) instead of PrimeNG/Bootstrap</td>
        </tr>
        <tr>
            <td>Chart.js</td>
            <td>^4.5.1</td>
            <td>Dashboard KPI charts (doughnut + stacked bar), used directly rather than via ng-apexcharts as in
                Admin/Customer</td>
        </tr>
        <tr>
            <td>pdfjs-dist</td>
            <td>^5.0.375</td>
            <td>Client-side PDF rendering — used only by the standalone "PDF Text Extractor" tool (not for stamp
                document rendering)</td>
        </tr>
        <tr>
            <td>xlsx</td>
            <td>^0.18.5</td>
            <td>Client-side Excel export only (allocation/transaction history reports) — no Excel import in this app
            </td>
        </tr>
        <tr>
            <td>TypeScript</td>
            <td>5.9</td>
            <td>Static typing</td>
        </tr>
        <tr>
            <td>Karma + Jasmine</td>
            <td>~6.4.0 / ~5.1.0</td>
            <td>Unit test runner/framework, configured the same way as Admin/Customer</td>
        </tr>
    </table>
    <p class="note">No Bootstrap, PrimeNG, RxJS-heavy state layer, CKEditor, or i18n/translation library is used in this
        app — it is a leaner, purpose-built operations tool compared to Admin/Customer's broad admin-theme dependency
        set.</p>

    <h2 id="s3">3. Project Structure</h2>

    <h3>3.1 Repository Layout</h3>
    <pre>esign_frontend_angular/
├── Admin/                     # Admin (staff) Angular app — Angular 17
├── Customer/                  # Customer (tenant) Angular app — Angular 19
├── server.js                  # Express host serving both apps by Host header
├── Dockerfile                 # Multi-stage build (admin-build, customer-build, runtime)
├── docker-compose.yml         # Single-service orchestration
├── package.json                # Root — Express host only (NOT an Angular project)
└── Task.md                    # (empty, gitignored)

estamp_frontend_angular/       # SEPARATE repository — Maker/Checker app, Angular 21
├── src/app/                   # See §3.3
├── server/                    # Its own static-file server directory (not analyzed in depth)
├── Dockerfile                 # Own single-app Docker build
├── docker-compose.yml         # Own orchestration, independent of esign_frontend_angular
└── package.json                # name: "e-stamp"</pre>

    <h3>3.2 Per-App Structure (identical pattern in Admin and Customer)</h3>
    <pre>&lt;App&gt;/src/app/
├── account/auth/               # Login, signup, password reset (lazy-loaded)
│   ├── login/  signup/  passwordreset/
│   └── auth-routing.ts, auth.module.ts
├── core/
│   ├── guards/                 # AuthGuard (+ eStampActiveGuard in Customer)
│   ├── helpers/                # jwt.interceptor.ts, error.interceptor.ts,
│   │                            #   fake-backend.ts (Admin), environment.interceptor.ts (Customer),
│   │                            #   http-context.tokens.ts
│   ├── models/                 # auth.models.ts (User class) — the ONLY file here in both apps
│   ├── services/                # ~15-18 feature/domain services (see §8)
│   └── core.module.ts          # Empty shell — all services are providedIn:'root'
├── layouts/
│   ├── layout/                 # LayoutComponent shell (vertical/horizontal switch)
│   ├── vertical/ horizontal/   # Layout wrapper components
│   └── shared/                 # sidebar, topbar, horizontaltopbar, horizontalnavbar,
│                                #   rightsidebar, footer, confirm-modal
├── pages/                      # Feature/route modules — see §9-13
│   ├── e-sign/  e-stamp/       # Core business modules
│   └── ...(dashboard, plans, settings, vendors, billing, account, etc. — app-specific)
├── shared/
│   ├── components/             # filter, pagination, toast, validation-message, status-page, etc.
│   ├── ui/                     # loader, pagetitle, confirm-modal (Customer)
│   ├── widget/                 # stat (KPI tile)
│   ├── pdf-signer/ view-pdf/   # PDF/signature tooling (Admin: shared/; Customer: shared/components/)
│   └── models/ utils/          # (Customer only) api-validation.model.ts, api-validation.util.ts
├── environments/               # environment.ts / environment.prod.ts
├── app.module.ts, app-routing.module.ts, app.component.ts, authUtils.ts
└── (angular.json, tsconfig*.json, karma.conf.js at app root)</pre>

    <h3>3.3 Maker/Checker App Structure (<code>estamp_frontend_angular/src/app/</code>)</h3>
    <p>A flatter, standalone-component layout — no per-feature NgModule wraps every component, only the two role modules
        (<code>MakerModule</code>, <code>CheckerModule</code>) exist as thin routing shells:</p>
    <pre>src/app/
├── core/
│   ├── guards/        # auth.guard.ts (role-aware), guest.guard.ts (currently a no-op)
│   ├── interceptors/  # api-base-url, auth, error (functional interceptors, not classes)
│   └── services/       # auth, dashboard, order, inventory, stamp, state, denomination,
│                        #   settings, toaster, confirmation-dialog, order-history, reports (stub), vendor (stub)
├── features/
│   ├── auth/           # login, register (role radio), forgot-password
│   ├── maker/          # dashboard, order, inventory, upload-stamp, settings
│   └── checker/        # dashboard, order, checker-settings, profile (stub)
├── layout/             # auth-layout (login/register shell), main-layout (header+sidebar shell)
├── shared/components/  # client-order, common-header, common-table, confirmation-dialog,
│                        #   footer, header, loader, pdf-text-extractor, sidebar, toaster, validation-message
├── environments/       # environment.ts / environment.prod.ts
└── app.routes.ts, app.config.ts   # standalone bootstrap — no app.module.ts</pre>
    <p>Notably, the order-list and order-detail screens (<code>shared/components/client-order/</code>) are
        <strong>shared between the Maker and Checker route trees</strong> rather than each role having its own
        implementation — this is the root cause of the Checker access gap documented in §12.
    </p>

    <h4>Responsibility of Key Folders</h4>
    <table>
        <tr>
            <th>Folder</th>
            <th>Responsibility</th>
        </tr>
        <tr>
            <td><code>core/services</code></td>
            <td>All HTTP API clients — one service per domain (customer, document, order, plan, template, e-stamp,
                dashboard, vendor, cron-job, settings, state, user, s3-storage, api-integration, billing, invoice,
                audit-trail, account)</td>
        </tr>
        <tr>
            <td><code>core/guards</code></td>
            <td>Route-level authentication/authorization checks</td>
        </tr>
        <tr>
            <td><code>core/helpers</code></td>
            <td>HTTP interceptors and interceptor-support tokens</td>
        </tr>
        <tr>
            <td><code>core/models</code></td>
            <td>Only holds <code>User</code> — nearly all other domain interfaces are declared inline inside their
                owning service file rather than centralized here</td>
        </tr>
        <tr>
            <td><code>layouts</code></td>
            <td>The authenticated-area chrome (sidebar navigation, topbar, theme panel) — mounted once around the routed
                <code>pages</code> module
            </td>
        </tr>
        <tr>
            <td><code>pages</code></td>
            <td>Route-bound feature components — one subfolder per business area, each with its own routing/NgModule
            </td>
        </tr>
        <tr>
            <td><code>shared</code></td>
            <td>Cross-feature reusable presentational components (filter bar, loader, pagination, toast, confirm modal,
                PDF tooling) with no page-specific business logic</td>
        </tr>
        <tr>
            <td><code>environments</code></td>
            <td>Build-time configuration swapped via Angular's <code>fileReplacements</code></td>
        </tr>
    </table>

    <h2 id="s4">4. Application Architecture</h2>

    <h3>4.1 Overall Architecture</h3>
    <p>Both apps follow a classic <strong>NgModule-based, feature-oriented</strong> Angular architecture (not standalone
        components, not a monorepo/Nx workspace — Admin and Customer are two completely separate Angular CLI projects
        with independent <code>angular.json</code>/<code>package.json</code>/<code>node_modules</code>). Each app is
        organized as: root module → lazy-loaded feature modules (<code>account</code>, <code>pages</code>, and within
        <code>pages</code>: <code>e-sign</code>, <code>e-stamp</code>) → route-bound components → shared/core
        cross-cutting modules.
    </p>

    <h3>4.2 Feature-Based Architecture</h3>
    <p>Feature areas (e-sign, e-stamp, and in Customer: billing/s3-storage/api-integration) are each self-contained
        NgModules with their own routing module, declared components, and (where needed) local PrimeNG/CKEditor imports
        — loaded via <code>loadChildren</code> lazy routes rather than being eagerly bundled into the main chunk.</p>

    <h3>4.3 Component Architecture</h3>
    <p>Standard Angular decorator-based components (<code>&#64;Component</code>, non-standalone in every file reviewed).
        Presentational/reusable pieces (filter bar, loader, pagetitle, stat tile, toast, confirm modal, PDF
        signer/viewer) are placed under <code>shared/</code> and communicate exclusively through
        <code>&#64;Input()</code>/<code>&#64;Output()</code> — no component-level shared services beyond the singleton
        core
        services.
    </p>

    <h3>4.4 Service Architecture</h3>
    <p>All services use <code>&#64;Injectable(&#123; providedIn: 'root' &#125;)</code>, i.e. tree-shakable singletons
        with no explicit
        provider arrays in any module (<code>core.module.ts</code> in both apps is an empty shell). One service class
        per backend domain, each holding its own relative endpoint paths — there is no shared generic
        HTTP-wrapper/base-service class; each service repeats its own <code>HttpParams</code>-building logic for
        pagination/filtering.</p>

    <h3>4.5 Dependency Injection</h3>
    <p>Standard Angular constructor injection throughout. One notable pattern: <code>ErrorInterceptor</code> resolves
        <code>ToastService</code> via the <code>Injector</code> token (rather than constructor injection) specifically
        to avoid a circular-DI failure between the interceptor and the service it needs.
    </p>

    <h3>4.6 Routing Architecture &amp; Lazy Loading</h3>
    <p>See §6 for the full route tree. Both apps use
        <code>RouterModule.forRoot(routes, &#123; scrollPositionRestoration: 'top' &#125;)</code> at the root, with the
        account/auth module and the main authenticated area
        (<code>pages</code>/<code>e-sign</code>/<code>e-stamp</code>) all lazy-loaded via <code>loadChildren</code>.
        Guards are applied at the parent-route level only (<code>AuthGuard</code> on the whole authenticated shell) — no
        child route in either app declares its own guard, with one confirmed gap in the Customer app (see §17).
    </p>

    <h3>4.7 Shared / Reusable Components</h3>
    <p>See §14 for the full inventory. Both apps re-implement near-identical shared components independently (filter
        bar, loader, pagetitle, toast, confirm modal, stat tile) — there is no shared library/package between Admin and
        Customer; each app owns a full copy.</p>

    <h3>4.8 Data Flow Between Layers</h3>
    <p>Component → injected <code>core/services/*.service.ts</code> method → <code>HttpClient</code> call → global
        interceptor chain (JWT attach → error normalize → base-URL prefix, order below in §16) → backend API → response
        mapped in the service (commonly adds <code>totalCount</code> from <code>res.meta.total</code> for PrimeNG
        pagination) → component subscribes and updates local component state → template re-renders. See §18 for the full
        diagram.</p>

    <h2 id="s5">5. Authentication and Authorization</h2>

    <h3>5.1 Admin App</h3>
    <table>
        <tr>
            <th>Aspect</th>
            <th>Implementation</th>
        </tr>
        <tr>
            <td>Login</td>
            <td><code>LoginComponent</code> (<code>account/auth/login/</code>) — password login
                (<code>AuthenticationService.login</code>, <code>POST /auth/admin/login-password</code>) and OTP login
                (<code>sendOtp</code>/<code>verifyOtp</code>, <code>POST /auth/send-otp</code> +
                <code>POST /auth/admin/login-otp</code>). Non-<code>'admin'</code> role responses are rejected
                client-side with a toast.
            </td>
        </tr>
        <tr>
            <td>Logout</td>
            <td><code>AuthenticationService.logout()</code> → <code>sessionStorage.removeItem('authUser')</code>.
                <strong>Inconsistency found:</strong> the horizontal-layout topbar's logout calls the legacy
                <code>AuthfakeauthenticationService.logout()</code> instead (clears <code>localStorage</code>, not the
                real session) in the default auth mode.
            </td>
        </tr>
        <tr>
            <td>Session storage</td>
            <td><code>sessionStorage.authUser</code> — a JSON blob of the full user object including
                <code>token</code>/<code>role</code>. No refresh token, no cookie-based session.
            </td>
        </tr>
        <tr>
            <td>Route guard</td>
            <td><code>AuthGuard</code> (<code>core/guards/auth.guard.ts</code>) — allows navigation only if
                <code>currentUser?.role === 'admin'</code>; else redirects to <code>/account/login</code> with a
                <code>returnUrl</code> query param.
            </td>
        </tr>
        <tr>
            <td>Role-based access</td>
            <td>Single binary check (<code>role === 'admin'</code>) gating the entire authenticated area; no granular
                permissions model. The sidebar menu is a static hardcoded array, not permission-filtered.</td>
        </tr>
        <tr>
            <td>HTTP interceptor</td>
            <td><code>JwtInterceptor</code> attaches <code>Authorization: Bearer &lt;token&gt;</code> to every request
                from the stored session.</td>
        </tr>
        <tr>
            <td>Unauthorized handling</td>
            <td><code>ErrorInterceptor</code>: on HTTP <code>401</code>, calls
                <code>AuthenticationService.logout()</code> (clears session) but performs no explicit redirect — the
                user is redirected only the next time <code>AuthGuard</code> runs.
            </td>
        </tr>
        <tr>
            <td>Token expiration</td>
            <td>No refresh-token flow, no expiry-based auto-redirect beyond the 401-triggered logout above.</td>
        </tr>
        <tr>
            <td>Password reset</td>
            <td><code>PasswordresetComponent</code> — dual-mode form: with a <code>token</code> query param it resets
                the password (<code>POST /auth/password-reset</code>); without one it requests a reset email
                (<code>POST /auth/password-reset-request</code>).</td>
        </tr>
    </table>
    <p class="gap"><strong>Redirect path mismatch (found in code):</strong> <code>AuthGuard</code> and both topbar
        logout handlers navigate to <code>/account/login</code>, but the actual routed path (per
        <code>AccountRoutingModule</code> + <code>AuthRoutingModule</code>) is <code>/account/auth/login</code>.
    </p>

    <h3>5.2 Customer App</h3>
    <table>
        <tr>
            <th>Aspect</th>
            <th>Implementation</th>
        </tr>
        <tr>
            <td>Login</td>
            <td><code>LoginComponent</code> — password (<code>AuthfakeauthenticationService.login</code>,
                <code>POST /auth/customer/login-password</code>) and OTP (<code>sendOtp</code>/<code>verifyOtp</code>,
                <code>POST /auth/send-otp</code> + <code>POST /auth/customer/login-otp</code>).
                Non-<code>'customer'</code> role is rejected client-side.
            </td>
        </tr>
        <tr>
            <td>Registration</td>
            <td><code>SignupComponent</code> — <code>AuthfakeauthenticationService.register()</code>
                (<code>POST /auth/register</code>, payload includes <code>createSubscription: true</code>) followed by
                mandatory OTP verification (<code>verifyRegistration</code>, <code>POST /auth/verify-otp</code>) before
                the account is usable; does not auto-login.</td>
        </tr>
        <tr>
            <td>Logout</td>
            <td><code>AuthfakeauthenticationService.logout()</code> →
                <code>sessionStorage.removeItem('currentUser')</code>.
            </td>
        </tr>
        <tr>
            <td>Session storage</td>
            <td><strong><code>sessionStorage.currentUser</code></strong> — despite its class name,
                <code>AuthfakeauthenticationService</code> is the real, active auth service in production (matches
                <code>environment.defaultauth = 'fackbackend'</code>). A second, mostly-dormant
                <code>AuthenticationService</code> exists for the Firebase code path and for password-reset endpoints.
            </td>
        </tr>
        <tr>
            <td>Route guard</td>
            <td><code>AuthGuard</code> — requires <code>currentUser.user.role === 'customer'</code>; protects the entire
                <code>/customer</code> route tree.
            </td>
        </tr>
        <tr>
            <td>Secondary guard</td>
            <td><code>eStampActiveGuard</code> (<code>core/guards/estamp.guard.ts</code>, a functional
                <code>CanMatchFn</code>) checks <code>AccountService.getEstampCustomerProfile()</code> and
                blocks/redirects if the tenant's e-stamp service isn't <code>ACTIVE</code>. <strong>It is imported in
                    <code>pages-routing.module.ts</code> but never attached to the <code>estamp</code> route</strong> —
                enforcement is done only at the UI level (sidebar click interception), not at the router level.
            </td>
        </tr>
        <tr>
            <td>HTTP interceptor</td>
            <td><code>JwtInterceptor</code> attaches <code>Authorization: Bearer &lt;access_token&gt;</code> from the
                stored session.</td>
        </tr>
        <tr>
            <td>Unauthorized handling</td>
            <td><code>ErrorInterceptor</code> shows a toast ("Your session has expired. Please login again.") on 401 but
                performs <strong>no automatic logout or redirect</strong>.</td>
        </tr>
        <tr>
            <td>Password reset</td>
            <td>Same dual-mode pattern as Admin, via <code>AuthenticationService.sendMailToResetPassword</code> /
                <code>resetPassword</code>.
            </td>
        </tr>
    </table>

    <h3>5.3 Maker/Checker App</h3>
    <table>
        <tr>
            <th>Aspect</th>
            <th>Implementation</th>
        </tr>
        <tr>
            <td>Login</td>
            <td><code>LoginComponent</code> — password only (no OTP option in this app):
                <code>AuthService.login()</code> → <code>POST auth/login-password</code>. <strong>Always navigates to
                    <code>/maker/dashboard</code> on success</strong> regardless of the returned role —
                <code>authGuard</code> then redirects Checker users on to <code>/checker/dashboard</code> when it
                detects the role mismatch on that route.
            </td>
        </tr>
        <tr>
            <td>Registration</td>
            <td><code>RegisterComponent</code> — name/email/password/confirm + a role radio (<code>MAKER</code> default,
                or <code>CHECKER</code>) → <code>POST auth/register</code>, followed by mandatory OTP verification
                (<code>POST auth/verify-otp</code>) before login is possible.</td>
        </tr>
        <tr>
            <td>Logout</td>
            <td><code>AuthService.logout()</code> → clears <strong>both</strong> <code>localStorage</code> and
                <code>sessionStorage</code>, then navigates to <code>/auth/login</code>.
            </td>
        </tr>
        <tr>
            <td>Session storage</td>
            <td><code>sessionStorage['authUser']</code> — JSON of <code>&#123; accessToken, user &#125;</code>.</td>
        </tr>
        <tr>
            <td>Route guard</td>
            <td><code>authGuard</code> (functional <code>CanActivateFn</code>) — redirects to <code>/auth/login</code>
                if no session; if the matched route declares <code>data.role</code>, compares it against
                <code>currentUser.user.role</code> and redirects to the <em>other</em> role's dashboard on mismatch
                (rather than to login).
            </td>
        </tr>
        <tr>
            <td>Secondary guard</td>
            <td><code>guestGuard</code> is wired onto <code>/auth</code> but its body is <strong>hardcoded to
                    <code>return true;</code></strong> — it performs no actual "already logged in, skip auth pages"
                redirect (a no-op / incomplete implementation).</td>
        </tr>
        <tr>
            <td>HTTP interceptor</td>
            <td><code>authInterceptor</code> attaches <code>Authorization: Bearer &lt;accessToken&gt;</code> from the
                stored session to every request.</td>
        </tr>
        <tr>
            <td>Unauthorized handling</td>
            <td><code>errorInterceptor</code> is a trivial pass-through (<code>catchError</code> that simply rethrows) —
                <strong>no centralized 401 handling, toast, or auto-logout at all</strong>; each component must handle
                its own error display via <code>ToasterService</code>.
            </td>
        </tr>
        <tr>
            <td>Password reset</td>
            <td><code>ForgotPasswordComponent</code> — same dual-mode pattern as Admin/Customer (request link vs. reset
                with <code>?token=</code>), via <code>POST auth/password-reset-request</code> /
                <code>POST auth/password-reset</code>.
            </td>
        </tr>
    </table>
    <p class="gap">Password-change requests from the <strong>Checker</strong> role are misrouted:
        <code>SettingsService.updateProfile()</code> and <code>changePassword()</code> have their endpoint URLs
        hardcoded to <code>maker/profile</code> / <code>bulk/maker/change-password</code> regardless of the
        <code>role</code> argument passed in — so a Checker's profile-update/password-change actions actually hit the
        Maker's backend endpoints. The read path (<code>getProfile(role)</code>) is correctly role-aware; only the two
        write methods have this bug.
    </p>

    <h3>5.4 Complete Authentication Flow (all three apps, common pattern)</h3>
    <ol>
        <li>User submits credentials (password or OTP) on the login screen.</li>
        <li>Auth service calls the backend, receives a user object + token/<code>access_token</code>.</li>
        <li>Client-side role check rejects mismatched roles (admin app requires <code>'admin'</code>, customer app
            requires <code>'customer'</code>) purely in the UI — no server-side role check is visible from the frontend
            code.</li>
        <li>Full response object is written to <code>sessionStorage</code> (key <code>authUser</code> in Admin,
            <code>currentUser</code> in Customer).
        </li>
        <li><code>JwtInterceptor</code> reads that stored object on every subsequent request to attach the bearer token.
        </li>
        <li><code>AuthGuard</code> reads the same stored object on every protected-route navigation.</li>
        <li>On 401 from any API call, <code>ErrorInterceptor</code> surfaces a toast (Admin additionally
            force-logs-out).</li>
    </ol>
    <p class="gap">Both auth services store the full plaintext session (including token) in <code>sessionStorage</code>,
        which is readable by any script running in the page (XSS-exposed) — see §25.</p>

    <h2 id="s6">6. Routing</h2>

    <h3>6.1 Admin App Route Tree</h3>
    <pre>/                                    (AppRoutingModule)
├── /account                        [lazy AccountModule]           (no guard)
│   └── /account/auth               [lazy AuthModule]
│       ├── /account/auth/login
│       ├── /account/auth/signup
│       └── /account/auth/change-password   (?token= query param)
└── ''  → LayoutComponent shell     [lazy PagesModule]  canActivate: [AuthGuard]
    ├── /dashboard  /analytics  /plans  /settings  /vendors
    ├── /email-settings  /logs  /cron-job
    ├── /esign        [lazy ESignModule]
    │   ├── /esign/customers
    │   ├── /esign/customers/:customerId
    │   ├── /esign/documents
    │   ├── /esign/orders
    │   └── /esign/document-template
    └── /estamp       [lazy EstampModule]
        ├── /estamp/customer
        ├── /estamp/inventory
        ├── /estamp/client-order
        ├── /estamp/client-order-detail/:id
        ├── /estamp/state
        └── /estamp/role</pre>

    <h3>6.2 Customer App Route Tree</h3>
    <pre>/status                              → StatusPageComponent (eager, no guard)
/account                             [lazy AccountModule]
    /account/auth                    [lazy AuthModule]
        /account/auth/login  /account/auth/signup  /account/auth/change-password (?token=)
''                                   [lazy FrontendModule] — public site
    ''  → HomeComponent
    /status/success  → StatusSuccessComponent  (?txn=&msg=)
    /status/failed   → StatusFailedComponent   (?txn=&msg=)
/customer  [AuthGuard, LayoutComponent shell]  [lazy PagesModule]
    /customer  → redirectTo 'dashboard'
    /customer/dashboard  /customer/account  /customer/api-integration
    /customer/billing  /customer/templates  /customer/s3-storage
    /customer/esign     [lazy ESignModule]
        /customer/esign/my-document
        /customer/esign/send-document
    /customer/estamp    [lazy EStampModule]
        /customer/estamp/create-order
        /customer/estamp/bulk-order
        /customer/estamp/create-customer-order
        /customer/estamp/customer-order-list
        /customer/estamp/inventory</pre>

    <h3>6.3 Route Guards, Parameters, Lazy Loading</h3>
    <ul>
        <li><strong>Lazy-loaded modules:</strong> every feature module in both apps (<code>AccountModule</code>,
            <code>AuthModule</code>, <code>PagesModule</code>,
            <code>ESignModule</code>/<code>EstampModule</code>/<code>EStampModule</code>, and Customer's
            <code>FrontendModule</code>) is loaded via <code>loadChildren</code>.
        </li>
        <li><strong>Protected routes:</strong> only the single top-level authenticated shell route (Admin's
            <code>''</code>, Customer's <code>customer</code>) carries <code>canActivate: [AuthGuard]</code>; no
            descendant route re-declares a guard.
        </li>
        <li><strong>Route parameters:</strong> Admin uses path params (<code>:customerId</code>, <code>:id</code>).
            Customer app declares <strong>no path parameters anywhere</strong> — IDs are passed via query params, router
            navigation <code>state</code>, or held in component/service memory instead.</li>
        <li><strong>Notable pattern:</strong> Admin's <code>ClientOrderDetailComponent</code> and Customer's flows rely
            on Angular Router navigation <code>state</code> (<code>history.state</code>) rather than re-fetching by
            route param — meaning a hard refresh or direct deep-link to that route yields an empty view (see §30).</li>
    </ul>

    <h3>6.4 Maker/Checker App Route Tree</h3>
    <pre>/auth                        [guestGuard — currently a no-op]  → AuthLayoutComponent
    /auth/login  /auth/register  /auth/forgot-password
/maker  [authGuard, data:&#123;role:'MAKER'&#125;]     → MainLayoutComponent  [lazy MakerModule]
    /maker/dashboard
    /maker/upload-stamp
    /maker/order
    /maker/order/:id            → BulkOrderDetailComponent (shared; always behaves as the Maker view)
    /maker/inventory
    /maker/settings
    /maker/pdf-text-extractor   (standalone, lazy — not linked from the sidebar)
/checker  [authGuard, data:&#123;role:'CHECKER'&#125;] → MainLayoutComponent  [lazy CheckerModule]
    /checker/dashboard
    /checker/order               (NOTE: no /checker/order/:id route is registered)
    /checker/profile             (stub page)
    /checker/settings
/main  → redirectTo '/maker'
''  and  **  → redirectTo '/auth/login'</pre>
    <p class="gap">Because no <code>/checker/order/:id</code> route exists, and the shared order list's "view" action
        unconditionally navigates to <code>maker/order/&#123;id&#125;</code> (a Maker-only route), a Checker user
        clicking "view"
        is bounced back to their dashboard by <code>authGuard</code> — <strong>Checkers cannot currently open an
            order-detail screen at all</strong> (see §12).</p>

    <h2 id="s7">7. Environment and Configuration</h2>

    <h3>7.1 Environment Files</h3>
    <table>
        <tr>
            <th>App</th>
            <th>File</th>
            <th>Key values</th>
        </tr>
        <tr>
            <td rowspan="2">Admin</td>
            <td><code>environment.ts</code> (dev)</td>
            <td><code>production: false</code>, <code>defaultauth: 'local'</code>,
                <code>apiUrl: 'https://devesignapi.ihostmyform.com/api'</code>,
                <code>avsLogger: &#123; project: 'avss-esign-backend', key: &lt;REDACTED&gt;, url: 'https://devlog.ihostmyform.com' &#125;</code>,
                empty <code>firebaseConfig</code>
            </td>
        </tr>
        <tr>
            <td><code>environment.prod.ts</code></td>
            <td><strong>Identical values to the dev file</strong> — still points at the dev API host and still has
                <code>production: false</code>. This is a configuration caveat, not a genuine production configuration.
            </td>
        </tr>
        <tr>
            <td rowspan="2">Customer</td>
            <td><code>environment.ts</code> (dev)</td>
            <td><code>production: false</code>, <code>apiUrl: 'https://devesignapi.ihostmyform.com/api'</code>,
                <code>esignAuditUrl: 'https://devesign3.ihostmyform.com'</code> (defined but never referenced in code),
                <code>otherUrl: ''</code>, <code>defaultauth: 'fackbackend'</code>
            </td>
        </tr>
        <tr>
            <td><code>environment.prod.ts</code></td>
            <td>Same values plus an empty <code>firebaseConfig</code> block; still <code>production: false</code> and
                still the dev API host.</td>
        </tr>
    </table>
    <p class="gap">Neither app's committed <code>environment.prod.ts</code> currently points at a distinct production
        API — both "production" builds bake in the dev backend URL. This must be corrected (or overridden via a
        deploy-time process not visible in this repository) before an actual production release.</p>

    <table>
        <tr>
            <th>App</th>
            <th>File</th>
            <th>Key values</th>
        </tr>
        <tr>
            <td rowspan="2">Maker/Checker</td>
            <td><code>environment.ts</code> (dev)</td>
            <td><code>production: false</code>, <code>apiUrl: 'https://devestampapi.ihostmyform.com/api/'</code> (two
                alternates commented out)</td>
        </tr>
        <tr>
            <td><code>environment.prod.ts</code></td>
            <td><code>production: true</code>, <code>apiUrl: 'https://devestampapi.ihostmyform.com/api'</code> —
                <strong>this is the only one of the three apps whose "production" environment file actually differs from
                    its dev file</strong> (flips <code>production</code> to <code>true</code>), though the API host is
                still the dev host.
            </td>
        </tr>
    </table>
    <p class="gap"><strong>Different backend host than Admin/Customer:</strong> the Maker/Checker app calls
        <code>devestampapi.ihostmyform.com</code>, while Admin and Customer call
        <code>devesignapi.ihostmyform.com</code>. Both operate on what is functionally the same e-stamp order record
        (shared <code>bsrId</code> and near-identical status vocabulary — see §11.5/§13.4), so this is either two
        gateways in front of one backend or two synchronized services; not resolvable from frontend code alone.
    </p>

    <h3>7.2 File-Replacement Mechanism</h3>
    <p>Standard Angular CLI mechanism: each app's <code>angular.json</code> declares, under the <code>production</code>
        build configuration,
        <code>fileReplacements: [&#123; replace: "src/environments/environment.ts", with: "src/environments/environment.prod.ts" &#125;]</code>.
        This only takes effect when the build is explicitly run as <code>ng build --configuration production</code>
        (which is what the Dockerfile does) — the bare <code>npm run build</code> script (<code>ng build</code>) uses
        the default/dev configuration.
    </p>

    <h3>7.3 Build Configurations (per app, both apps near-identical)</h3>
    <table>
        <tr>
            <th>Setting</th>
            <th>Default (<code>ng build</code>)</th>
            <th><code>production</code> configuration</th>
        </tr>
        <tr>
            <td>optimization</td>
            <td>false</td>
            <td>true</td>
        </tr>
        <tr>
            <td>sourceMap</td>
            <td>true</td>
            <td>false</td>
        </tr>
        <tr>
            <td>buildOptimizer</td>
            <td>false</td>
            <td>true</td>
        </tr>
        <tr>
            <td>outputHashing</td>
            <td>—</td>
            <td>all (drives long-lived cache headers in <code>server.js</code>)</td>
        </tr>
        <tr>
            <td>vendorChunk / namedChunks</td>
            <td>true</td>
            <td>false</td>
        </tr>
        <tr>
            <td>Budgets — Admin</td>
            <td colspan="2">initial 5MB warn / 8MB error; anyComponentStyle 500KB warn / 1MB error</td>
        </tr>
        <tr>
            <td>Budgets — Customer</td>
            <td colspan="2">initial 7MB warn / 8MB error; anyComponentStyle 500KB warn / 1MB error</td>
        </tr>
    </table>

    <h3>7.4 Runtime Server Configuration (<code>server.js</code>)</h3>
    <table>
        <tr>
            <th>Env var</th>
            <th>Default</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td><code>PORT</code></td>
            <td>3110</td>
            <td>Listen port</td>
        </tr>
        <tr>
            <td><code>ADMIN_HOST</code></td>
            <td>admin.example.com</td>
            <td>Comma-separated hostnames routed to the Admin build</td>
        </tr>
        <tr>
            <td><code>CUSTOMER_HOST</code></td>
            <td>customer.example.com</td>
            <td>Comma-separated hostnames routed to the Customer build</td>
        </tr>
        <tr>
            <td><code>ADMIN_DIST_PATH</code> / <code>CUSTOMER_DIST_PATH</code></td>
            <td><code>dist/admin</code> / <code>dist/customer</code></td>
            <td>Override for non-Docker/local runs</td>
        </tr>
    </table>
    <p><code>docker-compose.yml</code> sets <code>ADMIN_HOST=devesign-admin.ihostmyform.com</code>,
        <code>CUSTOMER_HOST=devesign.ihostmyform.com</code>, <code>PORT=3110</code> for the current dev deployment.
    </p>

    <h2 id="s8">8. API Integration</h2>

    <p>All service methods below call relative paths; the actual host is injected by an interceptor at the very end of
        the chain (Admin: implicit per-service <code>environment.apiUrl</code> prefix string-concatenated in each
        service; Customer: a dedicated <code>EnvironmentInterceptor</code> — see §16). Base URL (dev) =
        <code>https://devesignapi.ihostmyform.com/api</code>.
    </p>

    <h3>8.1 Admin App — API Surface by Service</h3>
    <table>
        <tr>
            <th>Service</th>
            <th>Method</th>
            <th>HTTP</th>
            <th>Endpoint</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td rowspan="7"><code>AuthenticationService</code></td>
            <td>login</td>
            <td>POST</td>
            <td><code>/auth/admin/login-password</code></td>
            <td>Password login</td>
        </tr>
        <tr>
            <td>sendOtp</td>
            <td>POST</td>
            <td><code>/auth/send-otp</code></td>
            <td>Request login OTP</td>
        </tr>
        <tr>
            <td>verifyOtp</td>
            <td>POST</td>
            <td><code>/auth/admin/login-otp</code></td>
            <td>OTP login</td>
        </tr>
        <tr>
            <td>register</td>
            <td>—</td>
            <td>Firebase SDK</td>
            <td>Only active if <code>defaultauth==='firebase'</code></td>
        </tr>
        <tr>
            <td>sendMailToResetPassword</td>
            <td>POST</td>
            <td><code>/auth/password-reset-request</code></td>
            <td>Forgot password</td>
        </tr>
        <tr>
            <td>resetPassword</td>
            <td>POST</td>
            <td><code>/auth/password-reset</code></td>
            <td>Reset with token</td>
        </tr>
        <tr>
            <td>logout</td>
            <td>—</td>
            <td>—</td>
            <td>Clears sessionStorage</td>
        </tr>
        <tr>
            <td rowspan="9"><code>CustomerService</code></td>
            <td>getCustomers</td>
            <td>GET</td>
            <td><code>/admin/customers</code></td>
            <td>Paginated tenant list</td>
        </tr>
        <tr>
            <td>getCustomerDetail</td>
            <td>GET</td>
            <td><code>/admin/customers/:id</code></td>
            <td>Tenant profile</td>
        </tr>
        <tr>
            <td>updateCustomer</td>
            <td>PATCH</td>
            <td><code>/admin/customers/:id</code></td>
            <td>Generic update (not called by read components)</td>
        </tr>
        <tr>
            <td>deactivateCustomer</td>
            <td>PATCH</td>
            <td><code>/admin/customers/:id/customerStatus</code></td>
            <td>Activate/deactivate tenant</td>
        </tr>
        <tr>
            <td>dashboard / getAnalyticsRevenue / getMonthlyDocStatus</td>
            <td>GET</td>
            <td><code>/admin/analytics/...</code></td>
            <td>Duplicates of <code>DashboardService</code> methods — unused (dead code)</td>
        </tr>
        <tr>
            <td>getEstampCustomers</td>
            <td>GET</td>
            <td><code>/bulk-estamp/clients</code></td>
            <td>E-stamp tenant list</td>
        </tr>
        <tr>
            <td>getClientConfig / updateClientConfig</td>
            <td>GET/PATCH</td>
            <td><code>/bulk-estamp/admin/client/:email/config</code></td>
            <td>Loss-policy &amp; service-charge config</td>
        </tr>
        <tr>
            <td>getAll / register</td>
            <td>GET/POST</td>
            <td><code>/api/login</code>, <code>/users/register</code></td>
            <td>Legacy, unprefixed, appear unused</td>
        </tr>
        <tr>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
        </tr>
        <tr>
            <td rowspan="2"><code>DashboardService</code></td>
            <td>dashboard / getAnalyticsRevenue / getMonthlyDocStatus</td>
            <td>GET</td>
            <td><code>/admin/analytics/dashboard</code>, <code>/revenue</code>, <code>/documents/:date</code></td>
            <td>E-Sign dashboard KPIs/charts</td>
        </tr>
        <tr>
            <td>getStampDashboardData / getAccessLogs</td>
            <td>GET / SDK</td>
            <td><code>/bulk-estamp/admin/dashboard</code>; <code>AVSLogger.getAccessLogs()</code></td>
            <td>E-Stamp dashboard; access-log widget</td>
        </tr>
        <tr>
            <td rowspan="2"><code>DocumentService</code></td>
            <td>getAllDocument</td>
            <td>GET</td>
            <td><code>/documents</code></td>
            <td>Platform-wide document list</td>
        </tr>
        <tr>
            <td>getDocumentDetail</td>
            <td>GET</td>
            <td><code>/documents/:id</code></td>
            <td>Document + signer detail</td>
        </tr>
        <tr>
            <td rowspan="7"><code>OrderService</code></td>
            <td>getOrders</td>
            <td>GET</td>
            <td><code>/orders/filter</code></td>
            <td>Order list (filter/search/customerId)</td>
        </tr>
        <tr>
            <td>getOrdersById</td>
            <td>GET</td>
            <td><code>/orders/:id</code></td>
            <td>Order detail</td>
        </tr>
        <tr>
            <td>createOrder</td>
            <td>POST</td>
            <td><code>/orders</code></td>
            <td>Create plan order</td>
        </tr>
        <tr>
            <td>completeOrder</td>
            <td>PATCH</td>
            <td><code>/orders/:id/complete</code></td>
            <td>Complete (Enterprise plans require extra fields)</td>
        </tr>
        <tr>
            <td>cancelOrder</td>
            <td>PATCH</td>
            <td><code>/orders/:id/cancel</code></td>
            <td>Cancel pending order</td>
        </tr>
        <tr>
            <td>getOrderStatics</td>
            <td>GET</td>
            <td><code>/orders/customer/:id/stats</code></td>
            <td>Defined, unused</td>
        </tr>
        <tr>
            <td>getCustomerOrders</td>
            <td>GET</td>
            <td><code>/orders/customer/:id</code></td>
            <td>Defined, unused</td>
        </tr>
        <tr>
            <td rowspan="5"><code>TemplateService</code></td>
            <td>getAllTemplates</td>
            <td>GET</td>
            <td><code>/templates</code></td>
            <td>List templates</td>
        </tr>
        <tr>
            <td>getAllTemplatesById</td>
            <td>GET</td>
            <td><code>/templates/:id</code></td>
            <td>Defined, unused</td>
        </tr>
        <tr>
            <td>createTemplate</td>
            <td>POST/PATCH</td>
            <td><code>/templates[/:id]</code></td>
            <td>Create/update (multipart)</td>
        </tr>
        <tr>
            <td>deleteTemplate</td>
            <td>DELETE</td>
            <td><code>/templates/:id</code></td>
            <td>Delete</td>
        </tr>
        <tr>
            <td>useTemplate</td>
            <td>POST</td>
            <td><code>/templates/use</code></td>
            <td>Defined, unused in Admin</td>
        </tr>
        <tr>
            <td rowspan="5"><code>PlanService</code></td>
            <td>getPlans</td>
            <td>GET</td>
            <td><code>/plans</code></td>
            <td>List subscription plans</td>
        </tr>
        <tr>
            <td>createPlan / updatePlan</td>
            <td>POST/PATCH</td>
            <td><code>/plans[/:id]</code></td>
            <td>CRUD</td>
        </tr>
        <tr>
            <td>updateStatus</td>
            <td>PATCH</td>
            <td><code>/plans/:id/status</code></td>
            <td>Activate/deactivate</td>
        </tr>
        <tr>
            <td>deactivatePlan</td>
            <td>PATCH</td>
            <td><code>/plans/:id/deactivate</code></td>
            <td>Defined, unused</td>
        </tr>
        <tr>
            <td rowspan="5"><code>EstampService</code></td>
            <td>getInventoryList</td>
            <td>GET</td>
            <td><code>/bulk-estamp/admin/scanned-stamps</code></td>
            <td>Inventory listing</td>
        </tr>
        <tr>
            <td>getClientOrders</td>
            <td>GET</td>
            <td><code>/bulk-estamp/admin/stamp-requests</code></td>
            <td>Bulk order listing</td>
        </tr>
        <tr>
            <td>generateBulkInvoice</td>
            <td>POST</td>
            <td><code>/bulk-estamp/admin/stamp-request/:id/generate-invoice</code></td>
            <td>Generate invoice</td>
        </tr>
        <tr>
            <td>orderProcess</td>
            <td>POST</td>
            <td><code>.../process-order</code></td>
            <td>Verify payment</td>
        </tr>
        <tr>
            <td>rejectPayment</td>
            <td>POST</td>
            <td><code>.../reject-payment</code></td>
            <td>Reject payment</td>
        </tr>
        <tr>
            <td rowspan="6"><code>UserProfileService</code></td>
            <td>getUsers</td>
            <td>GET</td>
            <td><code>/bulk-estamp/user-list</code></td>
            <td>Maker user list</td>
        </tr>
        <tr>
            <td>getUserDetail</td>
            <td>GET</td>
            <td><code>/bulk-estamp/user/:id</code></td>
            <td>Maker detail</td>
        </tr>
        <tr>
            <td>approvedStates</td>
            <td>PATCH</td>
            <td><code>/bulk-estamp/user/approve/:id</code></td>
            <td>Approve maker</td>
        </tr>
        <tr>
            <td>rejectedStateReq</td>
            <td>POST</td>
            <td><code>/bulk-estamp/user/reject</code></td>
            <td>Reject maker</td>
        </tr>
        <tr>
            <td>getStates</td>
            <td>GET</td>
            <td><code>/bulk-estamp/states</code></td>
            <td>State master list (accepts unused <code>role</code> param)</td>
        </tr>
        <tr>
            <td>assignMaker</td>
            <td>POST</td>
            <td><code>/bulk-estamp/client-user-mapping</code></td>
            <td>Assign maker to client</td>
        </tr>
        <tr>
            <td rowspan="4"><code>StateService</code></td>
            <td>getStates / createState / updateState / deleteState</td>
            <td>GET/POST/PATCH/DELETE</td>
            <td><code>/estamp/states</code>, <code>/estamp/state[/:id]</code></td>
            <td>State master CRUD</td>
        </tr>
        <tr>
            <td rowspan="4"><code>VendorService</code></td>
            <td>getVendors / createVendor / updateVendor / checkVendorHealth</td>
            <td>GET/POST/PATCH</td>
            <td><code>/admin/vendors[/:id][/health]</code></td>
            <td>Vendor integration management</td>
        </tr>
        <tr>
            <td rowspan="3"><code>SettingService</code></td>
            <td>getSettingData / updatePlatformConfig / updateSecuritySettings / updateComplianceSettings</td>
            <td>GET/PATCH</td>
            <td><code>/settings[/platform|security|compliance]</code></td>
            <td>Platform settings</td>
        </tr>
        <tr>
            <td rowspan="5"><code>EmailSttingsService</code></td>
            <td>getTemplates / createOrUpdateTemplate / getTemplateByCode / deleteTemplate / sendNotification /
                getNotificationLogs</td>
            <td>GET/POST/DELETE</td>
            <td><code>/notifications/templates[/:code]</code>, <code>/notifications/send</code>,
                <code>/notifications/logs</code>
            </td>
            <td>Multi-channel (email/SMS/WhatsApp) notification templates &amp; logs</td>
        </tr>
        <tr>
            <td><code>CronJobService</code></td>
            <td>full CRUD</td>
            <td>GET/POST/PATCH/DELETE</td>
            <td><code>/cron-job[/:id]</code></td>
            <td>Scheduled job management</td>
        </tr>
    </table>

    <h3>8.2 Customer App — API Surface by Service</h3>
    <table>
        <tr>
            <th>Service</th>
            <th>Method</th>
            <th>HTTP</th>
            <th>Endpoint</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td rowspan="6"><code>AuthfakeauthenticationService</code></td>
            <td>login</td>
            <td>POST</td>
            <td><code>/auth/customer/login-password</code></td>
            <td>Password login (real auth path)</td>
        </tr>
        <tr>
            <td>sendOtp / verifyOtp</td>
            <td>POST</td>
            <td><code>/auth/send-otp</code>, <code>/auth/customer/login-otp</code></td>
            <td>OTP login</td>
        </tr>
        <tr>
            <td>register / verifyRegistration</td>
            <td>POST</td>
            <td><code>/auth/register</code>, <code>/auth/verify-otp</code></td>
            <td>Signup + OTP confirmation</td>
        </tr>
        <tr>
            <td>switchEnv / getEnv</td>
            <td>POST/GET</td>
            <td><code>/esign/user-mode</code></td>
            <td>DEMO/LIVE mode toggle</td>
        </tr>
        <tr>
            <td rowspan="7"><code>AccountService</code></td>
            <td>getCustomerProfile</td>
            <td>GET</td>
            <td><code>/admin/customers/profile/me</code></td>
            <td>Own profile</td>
        </tr>
        <tr>
            <td>updateProfile / updatePassword / updateNotification / saveBranding</td>
            <td>PATCH/POST</td>
            <td><code>/admin/customers/:id/...</code></td>
            <td>Profile management</td>
        </tr>
        <tr>
            <td>getEstampCustomerProfile</td>
            <td>GET</td>
            <td><code>/bulk-estamp/client</code></td>
            <td>E-stamp activation status (used by guard/sidebar)</td>
        </tr>
        <tr>
            <td>getEstampClientConfig</td>
            <td>GET</td>
            <td><code>/bulk-estamp/admin/client/:email/config</code></td>
            <td>Loss-policy config (read)</td>
        </tr>
        <tr>
            <td>estampRequest</td>
            <td>PATCH</td>
            <td><code>/bulk-estamp/client/estamp-activation/request</code></td>
            <td>Request e-stamp activation</td>
        </tr>
        <tr>
            <td>getPlans</td>
            <td>GET</td>
            <td><code>/plans</code></td>
            <td>Plan catalogue (also used on public home page)</td>
        </tr>
        <tr>
            <td rowspan="8"><code>DocumentService</code></td>
            <td>getAllDocument / getDocumentDetail</td>
            <td>GET</td>
            <td><code>/documents</code>, <code>/documents/:id</code></td>
            <td>Document list/detail</td>
        </tr>
        <tr>
            <td>createDocument</td>
            <td>POST</td>
            <td><code>/documents/send[/:templateId]</code></td>
            <td>Send a new document for signing (multipart)</td>
        </tr>
        <tr>
            <td>resendMail</td>
            <td>GET</td>
            <td><code>/documents/resend-esign-email/:docId</code></td>
            <td>Resend signing invite</td>
        </tr>
        <tr>
            <td>checkDocumentStatus</td>
            <td>GET</td>
            <td><code>/documents/:id/status</code></td>
            <td>Poll signing status</td>
        </tr>
        <tr>
            <td>downloadDocument</td>
            <td>GET</td>
            <td><code>/documents/:id/download</code></td>
            <td>Get signed-document URL</td>
        </tr>
        <tr>
            <td>downloadDocumentAuditTrill</td>
            <td>GET (text)</td>
            <td><code>/documents/:id/audit</code></td>
            <td>Audit trail text/HTML</td>
        </tr>
        <tr>
            <td>documentStatusByTxn</td>
            <td>GET</td>
            <td><code>/documents/document-by-txn/:txnId</code></td>
            <td>Public success-page lookup</td>
        </tr>
        <tr>
            <td>signDocument</td>
            <td>POST</td>
            <td><code>/documents/:id/sign</code></td>
            <td>Defined, no call site found</td>
        </tr>
        <tr>
            <td rowspan="5"><code>TemplateService</code></td>
            <td>getAllTemplates / getAllTemplatesById</td>
            <td>GET</td>
            <td><code>/templates[/:id]</code></td>
            <td>List/detail</td>
        </tr>
        <tr>
            <td>createTemplate</td>
            <td>POST/PATCH</td>
            <td><code>/templates[/:id]</code></td>
            <td>Create/update (multipart)</td>
        </tr>
        <tr>
            <td>deleteTemplate</td>
            <td>DELETE</td>
            <td><code>/templates/:id</code></td>
            <td>Delete</td>
        </tr>
        <tr>
            <td>useTemplate</td>
            <td>POST</td>
            <td><code>/templates/use</code></td>
            <td>Defined, unused</td>
        </tr>
        <tr>
            <td rowspan="9"><code>EStampService</code></td>
            <td>getStates</td>
            <td>GET</td>
            <td><code>/bulk-estamp/state-list</code></td>
            <td>State dropdown</td>
        </tr>
        <tr>
            <td>getStampOrders / getStampOrderDetail</td>
            <td>GET</td>
            <td><code>/bulk-estamp/client/orders[/:id]</code></td>
            <td>Single/on-demand orders</td>
        </tr>
        <tr>
            <td>createOrder</td>
            <td>POST</td>
            <td><code>/bulk-estamp/order</code></td>
            <td>Create single order (multipart, +document)</td>
        </tr>
        <tr>
            <td>getBulkOrders / getBulkOrderDetail</td>
            <td>GET</td>
            <td><code>/bulk-estamp/client/stamp-requests[/:id]</code></td>
            <td>Bulk orders</td>
        </tr>
        <tr>
            <td>uploadBulkOrdersExcel</td>
            <td>POST</td>
            <td><code>/bulk-estamp/stamp-requests/upload</code></td>
            <td>Excel bulk order creation</td>
        </tr>
        <tr>
            <td>getInventoryList</td>
            <td>GET</td>
            <td><code>/bulk-estamp/client/scanned-stamps</code></td>
            <td>Customer inventory</td>
        </tr>
        <tr>
            <td>paymentSubmit / bulkPaymentSubmit</td>
            <td>POST</td>
            <td><code>/estamp/payment-proof</code>, <code>/bulk-estamp/stamp-requests/:id/payment-proof</code></td>
            <td>Payment proof upload</td>
        </tr>
        <tr>
            <td rowspan="3"><code>S3StorageService</code></td>
            <td>setupS3Bucket / connectS3Bucket / disconnectS3Bucket</td>
            <td>POST/GET</td>
            <td><code>/s3/setup</code>, <code>/s3/connect</code>, <code>/s3/disconnect</code></td>
            <td>Customer-owned S3 storage integration</td>
        </tr>
        <tr>
            <td>downloadS3Document / getS3Documents</td>
            <td>GET</td>
            <td><code>/s3/download/:id</code>, <code>/s3/documents</code></td>
            <td>Documents stored in customer's S3</td>
        </tr>
        <tr>
            <td rowspan="4"><code>ApiIntegrationService</code></td>
            <td>getApiList / getStampApiList</td>
            <td>GET</td>
            <td><code>/api-keys</code>, <code>/estamp-bulk/api-keys</code></td>
            <td>API key listing per product</td>
        </tr>
        <tr>
            <td>createApiKey / revokeApiKey</td>
            <td>POST/DELETE/PATCH</td>
            <td>same base (verb differs by product — DELETE for esign, PATCH for estamp)</td>
            <td>API key lifecycle</td>
        </tr>
        <tr>
            <td>webHookUrl</td>
            <td>PATCH</td>
            <td><code>/admin/customers/:id/webhook</code></td>
            <td>Webhook configuration</td>
        </tr>
        <tr>
            <td rowspan="5"><code>BillingService</code></td>
            <td>getSubscriptions / getPlans / getBillHistory</td>
            <td>GET</td>
            <td><code>/subscriptions</code>, <code>/plans</code>, <code>/subscriptions/invoices</code></td>
            <td>Billing data</td>
        </tr>
        <tr>
            <td>getCustomerOrders</td>
            <td>GET</td>
            <td><code>/orders/customer/:id</code></td>
            <td>Order history</td>
        </tr>
        <tr>
            <td>createPlan (used for upgrade)</td>
            <td>POST</td>
            <td><code>/orders</code></td>
            <td>Plan upgrade order</td>
        </tr>
        <tr>
            <td>downloadInvoice</td>
            <td>—</td>
            <td>—</td>
            <td><strong>Stubbed</strong> — returns a mocked success observable; real HTTP call is commented out</td>
        </tr>
    </table>

    <h3>8.3 Maker/Checker App — API Surface by Service</h3>
    <p>Base URL (dev) = <code>https://devestampapi.ihostmyform.com/api/</code> — a different host from Admin/Customer
        (see §7.1). Many methods take a <code>role</code> parameter (<code>'maker'</code>/<code>'checker'</code>) that
        is interpolated into the URL, so the same service backs both role's screens.</p>
    <table>
        <tr>
            <th>Service</th>
            <th>Method</th>
            <th>HTTP</th>
            <th>Endpoint</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td rowspan="6"><code>AuthService</code></td>
            <td>login</td>
            <td>POST</td>
            <td><code>auth/login-password</code></td>
            <td>Password login</td>
        </tr>
        <tr>
            <td>register</td>
            <td>POST</td>
            <td><code>auth/register</code></td>
            <td>Registration (with role)</td>
        </tr>
        <tr>
            <td>verifyOtp</td>
            <td>POST</td>
            <td><code>auth/verify-otp</code></td>
            <td>Registration OTP confirmation</td>
        </tr>
        <tr>
            <td>getUnassignedStates</td>
            <td>GET</td>
            <td><code>auth/states</code></td>
            <td>Defined, not currently invoked at registration</td>
        </tr>
        <tr>
            <td>forgotPassword / resetPassword</td>
            <td>POST</td>
            <td><code>auth/password-reset-request</code>, <code>auth/password-reset</code></td>
            <td>Password reset</td>
        </tr>
        <tr>
            <td>logout</td>
            <td>—</td>
            <td>—</td>
            <td>Clears localStorage + sessionStorage</td>
        </tr>
        <tr>
            <td rowspan="2"><code>DashboardService</code></td>
            <td>getDashboardData(params, role)</td>
            <td>GET</td>
            <td><code>bulk/&#123;role&#125;/dashboard</code></td>
            <td>Maker/Checker dashboard KPIs (optional <code>stateId</code> filter)</td>
        </tr>
        <tr>
            <td>getRecentInventory</td>
            <td>GET</td>
            <td><code>maker/recent-inventory</code></td>
            <td>Recent inventory widget</td>
        </tr>
        <tr>
            <td rowspan="5"><code>OrderService</code></td>
            <td>getBulkStampOrders(params, role)</td>
            <td>GET</td>
            <td><code>bulk/&#123;role&#125;/stamp-requests</code></td>
            <td>Order list (both roles)</td>
        </tr>
        <tr>
            <td>getBulkStampOrderById(id, role)</td>
            <td>GET</td>
            <td><code>bulk/&#123;role&#125;/stamp-request/:id</code></td>
            <td>Order detail</td>
        </tr>
        <tr>
            <td>allocateOrder / checkBulkStampInventory / fulfillBulkOrder / rejectBulkOrder</td>
            <td>POST/GET</td>
            <td><code>bulk/maker/stamp-request/:id/&#123;allocate-uploaded-stamps|inventory-check|allocate-stamps|reject-order&#125;</code>
            </td>
            <td>Maker order-processing actions — all hardcoded to the <code>maker</code> path regardless of caller</td>
        </tr>
        <tr>
            <td>updateStatus</td>
            <td>POST</td>
            <td><code>&#123;role&#125;/verify</code></td>
            <td>Approve/reject verb — defined for Checker use, but no component currently calls it (see §12)</td>
        </tr>
        <tr>
            <td>checkerRemark</td>
            <td>POST</td>
            <td><code>checker/order/:orderId/remark</code></td>
            <td>Defined for Checker use, no call site found</td>
        </tr>
        <tr>
            <td rowspan="4"><code>InventoryService</code></td>
            <td>getInventory</td>
            <td>GET</td>
            <td><code>bulk/maker/scanned-stamps</code></td>
            <td>Maker's inventory list</td>
        </tr>
        <tr>
            <td>getInventoryDetail</td>
            <td>GET</td>
            <td><code>bulk/maker/stamp-request/:id</code></td>
            <td>Single item detail</td>
        </tr>
        <tr>
            <td>approveInventorySatus / defaceInventorySatus</td>
            <td>PATCH</td>
            <td><code>maker/stamp-approve/:id</code>, <code>maker/stamp-deface/:id</code></td>
            <td>Approve/deface a scanned item (method names carry a typo as written in source)</td>
        </tr>
        <tr>
            <td>delteInventory</td>
            <td>DELETE</td>
            <td><code>maker/stamp-inventory/:id</code></td>
            <td>Delete an item (typo as written in source)</td>
        </tr>
        <tr>
            <td><code>StampService</code></td>
            <td>uploadBulkStamps(data, orderId)</td>
            <td>POST</td>
            <td><code>bulk/maker/scanned-stamps/upload/:orderId</code></td>
            <td>Bulk scanned stamp <strong>PDF</strong> upload against an order</td>
        </tr>
        <tr>
            <td rowspan="3"><code>SettingsService</code></td>
            <td>getProfile(role)</td>
            <td>GET</td>
            <td><code>bulk/&#123;role&#125;/profile</code></td>
            <td>Correctly role-aware</td>
        </tr>
        <tr>
            <td>updateProfile / changePassword</td>
            <td>PUT/POST</td>
            <td><code>maker/profile</code>, <code>bulk/maker/change-password</code></td>
            <td class="gap">Hardcoded to <code>maker</code> regardless of the caller's actual role (see §5.3 gap)</td>
        </tr>
        <tr>
            <td><code>StateService</code> / <code>DenominationService</code></td>
            <td>full CRUD</td>
            <td>GET/POST/PATCH/DELETE</td>
            <td><code>state/maker</code>, <code>state[/:id]</code>, <code>denomination[/:id]</code></td>
            <td>Master data</td>
        </tr>
    </table>
    <p><code>ReportsService</code> and <code>VendorService</code> in this app are empty stubs with no implemented
        methods. <code>OrderHistoryService</code> performs no HTTP calls at all — it is a pure client-side Excel/HTML
        invoice export utility (via the <code>xlsx</code> library and a hidden print <code>&lt;iframe&gt;</code>).</p>

    <h3>8.4 Authentication Requirements</h3>
    <p>Every service call above rides through <code>JwtInterceptor</code>, which attaches
        <code>Authorization: Bearer &lt;token&gt;</code> whenever a session exists — there is no per-endpoint
        distinction in the frontend code between public and protected calls (public endpoints, e.g. the home page's
        <code>getPlans()</code>, simply receive no token because no session exists yet).
    </p>

    <h3>8.5 Error Handling (API layer)</h3>
    <p>Individual services rarely add their own <code>catchError</code>; error normalization is centralized in
        <code>ErrorInterceptor</code> (see §16/§21). A subset of write operations (document/template creation, e-stamp
        order creation, S3 setup) opt out of the global error toast via
        <code>HttpContext(SKIP_GLOBAL_ERROR_TOAST, true)</code> so the component can render an inline validation panel
        instead (<code>ApiValidationMessageComponent</code> in Customer, similar inline patterns in Admin).
    </p>

    <h2 id="s9">9. Feature and Module Documentation</h2>
    <p>This section indexes every top-level feature module found in the source. Full behavioral detail for E-Sign and
        E-Stamp is in §10/§11; the remaining modules are summarized here.</p>

    <h3>9.1 Admin App Modules</h3>
    <table>
        <tr>
            <th>Module</th>
            <th>Route</th>
            <th>Purpose</th>
            <th>Key APIs</th>
        </tr>
        <tr>
            <td>Dashboard</td>
            <td><code>/dashboard</code></td>
            <td>Tab shell switching between E-Sign / E-Stamp dashboards</td>
            <td>delegated to child dashboards</td>
        </tr>
        <tr>
            <td>Analytics</td>
            <td><code>/analytics</code></td>
            <td>Mostly static/mock KPI cards plus 4 embedded Metabase BI iframes (Error Monitoring, Project Health, API
                Performance, Security Access)</td>
            <td>none (mock data)</td>
        </tr>
        <tr>
            <td>Access Logs</td>
            <td><code>/logs</code></td>
            <td>API access-log table via the AVSLogger SDK</td>
            <td><code>AVSLogger.getAccessLogs()</code></td>
        </tr>
        <tr>
            <td>Cron Job</td>
            <td><code>/cron-job</code></td>
            <td>CRUD for scheduled HTTP jobs (name/url/method/schedule)</td>
            <td><code>/cron-job</code></td>
        </tr>
        <tr>
            <td>Plans</td>
            <td><code>/plans</code></td>
            <td>Subscription plan CRUD + activate/deactivate</td>
            <td><code>/plans</code></td>
        </tr>
        <tr>
            <td>Settings</td>
            <td><code>/settings</code></td>
            <td>Platform/security/compliance configuration panels</td>
            <td><code>/settings/*</code></td>
        </tr>
        <tr>
            <td>Vendors</td>
            <td><code>/vendors</code></td>
            <td>Third-party vendor CRUD + health check</td>
            <td><code>/admin/vendors</code></td>
        </tr>
        <tr>
            <td>Email/Notification Settings</td>
            <td><code>/email-settings</code></td>
            <td>Multi-channel notification template manager, manual send, logs</td>
            <td><code>/notifications/*</code></td>
        </tr>
        <tr>
            <td>E-Sign</td>
            <td><code>/esign/*</code></td>
            <td>See §10</td>
            <td>—</td>
        </tr>
        <tr>
            <td>E-Stamp</td>
            <td><code>/estamp/*</code></td>
            <td>See §11/§12/§13</td>
            <td>—</td>
        </tr>
    </table>

    <h3>9.2 Customer App Modules</h3>
    <table>
        <tr>
            <th>Module</th>
            <th>Route</th>
            <th>Purpose</th>
            <th>Key APIs</th>
        </tr>
        <tr>
            <td>Public site</td>
            <td><code>/</code></td>
            <td>Marketing home page (plan catalogue), post-signing success/failure pages</td>
            <td><code>/plans</code>, <code>/documents/document-by-txn/:id</code></td>
        </tr>
        <tr>
            <td>Dashboard</td>
            <td><code>/customer/dashboard</code></td>
            <td>Tab shell (E-Sign/E-Stamp), blocks the E-Stamp tab client-side unless the tenant's e-stamp service is
                <code>ACTIVE</code>
            </td>
            <td><code>/bulk-estamp/client</code></td>
        </tr>
        <tr>
            <td>Account</td>
            <td><code>/customer/account</code></td>
            <td>Profile, password, notifications, branding, e-stamp activation request</td>
            <td><code>/admin/customers/...</code></td>
        </tr>
        <tr>
            <td>Billing</td>
            <td><code>/customer/billing</code></td>
            <td>Subscription view, plan upgrade (client-generated pseudo transaction id, COD-only), order history,
                invoice print</td>
            <td><code>/subscriptions</code>, <code>/orders</code></td>
        </tr>
        <tr>
            <td>API &amp; Integration</td>
            <td><code>/customer/api-integration</code></td>
            <td>API key management (per product), webhook URL config, cURL/Postman documentation export</td>
            <td><code>/api-keys</code>, <code>/estamp-bulk/api-keys</code></td>
        </tr>
        <tr>
            <td>S3 Storage</td>
            <td><code>/customer/s3-storage</code></td>
            <td>Bring-your-own-S3-bucket document storage setup/connect/browse/download</td>
            <td><code>/s3/*</code></td>
        </tr>
        <tr>
            <td>Templates</td>
            <td><code>/customer/templates</code></td>
            <td>Reusable e-sign document templates</td>
            <td><code>/templates</code></td>
        </tr>
        <tr>
            <td>E-Sign</td>
            <td><code>/customer/esign/*</code></td>
            <td>See §10</td>
            <td>—</td>
        </tr>
        <tr>
            <td>E-Stamp</td>
            <td><code>/customer/estamp/*</code></td>
            <td>See §11/§13</td>
            <td>—</td>
        </tr>
    </table>

    <h3>9.3 Maker/Checker App Modules</h3>
    <table>
        <tr>
            <th>Module</th>
            <th>Route</th>
            <th>Purpose</th>
            <th>Key APIs</th>
        </tr>
        <tr>
            <td>Auth</td>
            <td><code>/auth/*</code></td>
            <td>Login, registration (with role selection), forgot/reset password</td>
            <td><code>auth/*</code></td>
        </tr>
        <tr>
            <td>Maker Dashboard</td>
            <td><code>/maker/dashboard</code></td>
            <td>Inventory &amp; order KPI cards, status charts</td>
            <td><code>bulk/maker/dashboard</code></td>
        </tr>
        <tr>
            <td>Maker Order</td>
            <td><code>/maker/order[/:id]</code></td>
            <td>Process bulk stamp orders end-to-end (allocate, check inventory, fulfil, reject)</td>
            <td><code>bulk/maker/stamp-requests</code>, <code>bulk/maker/stamp-request/:id/*</code></td>
        </tr>
        <tr>
            <td>Upload Stamp</td>
            <td><code>/maker/upload-stamp</code></td>
            <td>Bulk upload of scanned stamp PDF files against an order</td>
            <td><code>bulk/maker/scanned-stamps/upload/:orderId</code></td>
        </tr>
        <tr>
            <td>Inventory</td>
            <td><code>/maker/inventory</code></td>
            <td>List scanned stamps; approve/deface/delete individual items while status is <code>NEW</code></td>
            <td><code>bulk/maker/scanned-stamps</code>, <code>maker/stamp-approve/:id</code>,
                <code>maker/stamp-deface/:id</code>
            </td>
        </tr>
        <tr>
            <td>Settings</td>
            <td><code>/maker/settings</code></td>
            <td>Profile + change password</td>
            <td><code>bulk/maker/profile</code>, <code>bulk/maker/change-password</code></td>
        </tr>
        <tr>
            <td>PDF Text Extractor</td>
            <td><code>/maker/pdf-text-extractor</code></td>
            <td>Standalone tool, hidden from the sidebar — draws regions on a rendered PDF and extracts text via an
                external API</td>
            <td>External: <code>extract-api.avsstech.in/extract-text</code></td>
        </tr>
        <tr>
            <td>Checker Dashboard</td>
            <td><code>/checker/dashboard</code></td>
            <td>Same KPI layout as Maker, scoped to checker data — read-only</td>
            <td><code>bulk/checker/dashboard</code></td>
        </tr>
        <tr>
            <td>Checker Order</td>
            <td><code>/checker/order</code></td>
            <td>Order list only — no detail/approve screen reachable (see §12)</td>
            <td><code>bulk/checker/stamp-requests</code></td>
        </tr>
        <tr>
            <td>Checker Settings</td>
            <td><code>/checker/settings</code></td>
            <td>Reuses the Maker settings component; write actions misrouted to Maker endpoints</td>
            <td>See §5.3 gap</td>
        </tr>
        <tr>
            <td>Checker Profile</td>
            <td><code>/checker/profile</code></td>
            <td class="gap">Unimplemented placeholder page (literal
                <code>&lt;p&gt;Checker Profile works!&lt;/p&gt;</code>)
            </td>
            <td>none</td>
        </tr>
    </table>

    <h2 id="s10">10. E-Sign Module</h2>

    <h3>10.1 Customer App — Full Authoring Workflow (primary implementation)</h3>
    <ol>
        <li><strong>Start:</strong> from scratch (<code>SendDocumentComponent</code>) or from a saved template
            (<code>Use</code> action on <code>TemplatesComponent</code> → navigates with <code>?templateId=</code>).
        </li>
        <li><strong>Document + signer setup</strong> (CDK stepper step 1): reactive form (<code>name</code>,
            <code>description</code>, <code>livePhoto</code> toggle, <code>signingOrder</code> default
            <code>'sequential'</code>, <code>message</code>) plus a <code>FormArray</code> of signers, each requiring
            <code>name</code>, <code>email</code> (with a duplicate-domain-blocking validator), <code>esignType</code>
            (<strong>1=Aadhar, 2=DSC, 3=Aadhar Biometric</strong>), and <code>phone</code> (Indian mobile pattern). PDF
            upload validated ≤10.1MB and MIME <code>application/pdf</code>. Signers are drag-and-drop reorderable.
        </li>
        <li><strong>Signature placement</strong> (step 2, per signer): <code>PdfSignerComponent</code> renders the PDF
            via <code>pdfjs-dist</code> and lets the current signer double-click/drag/resize rectangular signature
            boxes; other signers' already-placed boxes are shown read-only. Positions are stored in PDF-point units.
        </li>
        <li><strong>Submission:</strong> full client-side validation (name, file, all recipient fields, every signer has
            ≥1 signature position) → confirmation modal → <code>POST /documents/send[/:templateId]</code> (multipart).
        </li>
        <li><strong>Tracking</strong> (<code>MyDocumentComponent</code>): paginated/filterable list
            (<code>Pending</code>/<code>Signed</code>), per-signer completion % computed client-side, status polling
            (<code>GET /documents/:id/status</code>).</li>
        <li><strong>Signing continuation:</strong> "Continue signing" opens the current signer's
            <code>redirect_esign_url</code> (returned by the backend) in a new tab — <strong>actual Aadhaar OTP / DSC /
                biometric authentication happens on an external, e-sign-provider-hosted page, outside this Angular
                application.</strong> The UI enforces sequential completion by only revealing the next signer's link
            once available.
        </li>
        <li><strong>Completion:</strong> <code>downloadAndPreview()</code> → <code>GET /documents/:id/download</code> →
            opens the returned <code>signDocumentUrl</code> (final signed PDF).</li>
        <li><strong>Audit trail:</strong> <code>GET /documents/:id/audit</code> (a real, working endpoint) returns
            text/HTML opened in a new tab. Separately, a standalone
            <code>AuditTrailComponent</code>/<code>AuditTrailService</code> exists but currently renders
            <strong>hardcoded mock data</strong> (not wired to a live endpoint) — see §30.
        </li>
    </ol>

    <h3>10.2 DSC / Signature-Related Functionality</h3>
    <p>The frontend implements <strong>signature-method selection and placement only</strong> — no client-side
        cryptographic signing, PKI/PKCS11/USB-token handling, or OTP-entry UI exists anywhere in the codebase. What is
        implemented:</p>
    <ul>
        <li>Per-signer <code>esignType</code> selection: Aadhaar OTP (1), DSC (2), Aadhaar Biometric (3).</li>
        <li>Visual signature-box placement/preview using <code>pdfjs-dist</code> (render) + <code>pdf-lib</code> (burns
            placeholder rectangles + "Sign here" labels into a preview copy of the PDF) — this is layout tooling, not an
            applied cryptographic signature.</li>
        <li>The mock audit-trail data (illustrative of the real backend's event vocabulary) shows activities like
            <code>"eSigned using Aadhaar"</code> (with <code>authenticationType: "One Time Password"</code>) and
            <code>"eSigned using DSC"</code> (with a certificate block: serial no., issuer, organization, validity
            window) — confirming the backend performs the actual DSC/Aadhaar signing, external to this frontend.
        </li>
    </ul>

    <h3>10.3 Templates</h3>
    <p>Templates let a user pre-configure up to 5 placeholder signers (no contact info required) with pre-placed
        signature positions on a base PDF. <code>POST/PATCH /templates[/:id]</code> (multipart, fields:
        <code>name, category, description, allSignersPositions (JSON), file</code>). Category enum:
        <code>Fintech, Real Estate, Healthcare, Legal</code>. View mode uses <code>ViewPdfComponent</code>, which always
        burns all signer positions into a preview PDF before display.
    </p>

    <h3>10.4 Admin App — Oversight (not authoring)</h3>
    <table>
        <tr>
            <th>Screen</th>
            <th>Capability</th>
        </tr>
        <tr>
            <td>Customers (<code>/esign/customers</code>)</td>
            <td>List/filter tenants; activate/deactivate; drill into a tenant's profile + order history; assign a new
                plan/order (COD-only, client-generated transaction id)</td>
        </tr>
        <tr>
            <td>Documents (<code>/esign/documents</code>)</td>
            <td><strong>Read-only</strong> platform-wide document list + detail modal with per-signer status and
                completion progress bar. No create/edit/delete of documents in the Admin app.</td>
        </tr>
        <tr>
            <td>Orders (<code>/esign/orders</code>)</td>
            <td>List/filter plan-purchase orders; Complete (Enterprise plans require manual
                amount/users/documents/endDate entry) or Cancel a <code>Pending</code> order</td>
        </tr>
        <tr>
            <td>Templates (<code>/esign/document-template</code>)</td>
            <td>Full CRUD identical in capability to the Customer app's template editor, using the same
                <code>PdfSignerComponent</code>/<code>ViewPdfComponent</code> tooling
            </td>
        </tr>
    </table>
    <p><strong>Order status enum (Admin, plan orders):</strong> <code>Pending</code>, <code>Completed</code>,
        <code>Cancelled</code>.<br />
        <strong>Document status (filter):</strong> <code>Pending</code>, <code>Signed</code> (per-signer status compared
        case-insensitively).
    </p>
    <p class="na">The Maker/Checker app has no E-Sign functionality whatsoever — it is scoped entirely to E-Stamp
        fulfilment (§12).</p>

    <h2 id="s11">11. E-Stamp Module</h2>

    <h3>11.1 Order Types</h3>
    <ul>
        <li><strong>Single/on-demand order</strong> (Customer: <code>create-customer-order</code> →
            <code>customer-order-list</code>): one document + stamp metadata (state, denomination, purpose, first/second
            party, stamp position) submitted via <code>POST /bulk-estamp/order</code> (multipart, includes the source
            document).
        </li>
        <li><strong>Bulk order via Excel upload</strong> (Customer: <code>upload-stamp-excel</code> →
            <code>bulk-order</code>): user downloads a static template file, uploads an
            <code>.xlsx</code>/<code>.xls</code>/<code>.csv</code> (≤1MB), the file is <strong>client-side
                pre-validated</strong> using the <code>xlsx</code> library (see §11.4), then the raw file is uploaded to
            <code>POST /bulk-estamp/stamp-requests/upload</code> for authoritative server-side creation.
        </li>
    </ul>

    <h3>11.2 Invoice Generation</h3>
    <p>Admin generates the invoice for a bulk order once it is in <code>ORDER_CREATED</code> status:
        <code>POST /bulk-estamp/admin/stamp-request/:id/generate-invoice</code>. Customer-side,
        <code>InvoiceService.getStampOrderInvoice()</code> is a <strong>pure client-side HTML/print generator</strong>
        (no HTTP call) that renders a "Bill Receipt" from the order's line items and the fetched invoice object, printed
        via a hidden iframe (desktop) or opened as a Blob in a new tab (mobile). It hard-codes the text "Organisation:
        Axis Bank" regardless of the actual tenant.
    </p>

    <h3>11.3 Payment Proof Upload &amp; Verification</h3>
    <table>
        <tr>
            <th>Step</th>
            <th>Actor</th>
            <th>API</th>
            <th>Validation</th>
        </tr>
        <tr>
            <td>Upload payment proof (single order)</td>
            <td>Customer</td>
            <td><code>POST /estamp/payment-proof</code></td>
            <td>JPG/PNG/JPEG only, ≤2MB</td>
        </tr>
        <tr>
            <td>Upload payment proof (bulk order)</td>
            <td>Customer</td>
            <td><code>POST /bulk-estamp/stamp-requests/:id/payment-proof</code></td>
            <td>Same file rules</td>
        </tr>
        <tr>
            <td>Verify payment</td>
            <td>Admin</td>
            <td><code>POST /bulk-estamp/admin/stamp-request/:id/process-order</code></td>
            <td>Non-empty reason text required client-side</td>
        </tr>
        <tr>
            <td>Reject payment</td>
            <td>Admin</td>
            <td><code>POST /bulk-estamp/admin/stamp-request/:id/reject-payment</code></td>
            <td>Non-empty reason text required client-side</td>
        </tr>
    </table>

    <h3>11.4 Bulk Excel Upload — Client-Side Validation Rules</h3>
    <p>Implemented in <code>UploadStampExcel</code> (Customer) using the <code>xlsx</code> library
        (<code>FileReader</code> → <code>XLSX.read</code> → <code>sheet_to_json</code> on the first sheet):</p>
    <ol>
        <li>File must not be empty.</li>
        <li>Required columns (case-insensitive header match): <code>State</code>, <code>Denomination</code>,
            <code>Quantity</code>.
        </li>
        <li>Per row: <code>State</code> non-empty; <code>Denomination</code> matches <code>^\d+(\.\d+)?$</code> and is
            &gt; 0; <code>Quantity</code> matches <code>^\d+$</code> (integer) and is ≥ 1.</li>
        <li><strong>Duplicate detection:</strong> any repeated <code>State + Denomination</code> combination across rows
            is rejected with an instruction to combine the rows via <code>Quantity</code> instead.</li>
        <li>Estimated order total (<code>Σ denomination × quantity</code>) is computed and shown before submission
            (server computes the authoritative total).</li>
    </ol>
    <p class="note">This is a pre-submission UX check only — the raw file is still uploaded and the backend performs its
        own authoritative validation (the response can return partial success with a per-row <code>errors[]</code>
        array).</p>

    <h3>11.5 Order/Inventory/Invoice Status Vocabulary (as found in code)</h3>
    <table>
        <tr>
            <th>Domain</th>
            <th>Values</th>
        </tr>
        <tr>
            <td>Bulk order status (filterable in UI)</td>
            <td><code>ORDER_CREATED</code>, <code>ORDER_REJECTED</code>, <code>INVOICE_GENERATED</code>,
                <code>PAYMENT_SUBMITTED</code>, <code>PAYMENT_REJECTED</code>, <code>ORDER_PROCESSING</code>,
                <code>PARTIAL_FULFILLED</code>, <code>ORDER_FULFILLED</code>
            </td>
        </tr>
        <tr>
            <td>Bulk order status (styling-only, seen but not filterable)</td>
            <td><code>UNDER_REVIEW</code> / <code>UNDER_CHECKER_REVIEW</code>, <code>PAYMENT_VERIFIED</code>,
                <code>ESTAMP_UPLOADED</code>, <code>COMPLETED</code>
            </td>
        </tr>
        <tr>
            <td>Inventory status</td>
            <td><code>AVAILABLE</code>, <code>RESERVED</code> (dashboard only), <code>ALLOCATED</code>,
                <code>UTILIZED</code> (Admin exposes 3 of 4 as a filter; Customer exposes only
                <code>UTILIZED</code>/<code>ALLOCATED</code>)
            </td>
        </tr>
        <tr>
            <td>Invoice status</td>
            <td><code>GENERATED</code>, <code>PAID</code>, <code>CANCELLED</code></td>
        </tr>
        <tr>
            <td>Maker (user) status</td>
            <td><code>PENDING</code>, <code>ACTIVE</code>, <code>REJECTED</code></td>
        </tr>
        <tr>
            <td>Customer e-stamp service status</td>
            <td><code>ACTIVE</code>, <code>INACTIVE</code>, <code>REQUESTED</code>, <code>PENDING</code></td>
        </tr>
    </table>
    <p class="gap">There is no declared state-machine in the frontend code — the above lists are UI dropdown/badge
        metadata, not an enforced transition graph. The status set referenced across components is inconsistent (e.g.
        <code>InventoryComponent</code>'s badge-color function includes order statuses irrelevant to inventory — see
        §30).
    </p>

    <h2 id="s12">12. Maker and Checker Modules</h2>
    <p class="note">A dedicated Maker/Checker web application <strong>does exist</strong> — it lives in the separate
        <code>estamp_frontend_angular</code> repository, not inside <code>esign_frontend_angular</code>. It talks to a
        different backend host (<code>devestampapi.ihostmyform.com</code>, §7.1) than Admin/Customer but operates on the
        same order records (shared <code>bsrId</code> and status vocabulary).
    </p>

    <h3>12.1 Maker Onboarding (Admin side, unchanged)</h3>
    <ul>
        <li>A user registers directly in the Maker/Checker app choosing role <code>MAKER</code> or <code>CHECKER</code>
            (§5.3) — there is no invite-only provisioning flow.</li>
        <li>Separately, Admin staff manage the same maker-user records via the Admin app's <code>/estamp/role</code>
            screen (<code>UserComponent</code>): <code>GET /bulk-estamp/user-list</code> → detail
            (<code>GET /bulk-estamp/user/:id</code>) → Approve (<code>PATCH /bulk-estamp/user/approve/:id</code>) or
            Reject with a reason (<code>POST /bulk-estamp/user/reject</code>).</li>
        <li>A maker is then <strong>assigned to a specific client account</strong> by Admin via
            <code>EstampCustomerComponent</code>'s "Assign Maker" action
            (<code>POST /bulk-estamp/client-user-mapping</code>), only available while that client's
            <code>estampService</code> status is <code>REQUESTED</code>.
        </li>
    </ul>

    <h3>12.2 Maker Module — Fully Functional</h3>
    <table>
        <tr>
            <th>Screen</th>
            <th>Capability</th>
        </tr>
        <tr>
            <td>Dashboard</td>
            <td>Inventory &amp; order KPI cards; doughnut chart (inventory by status) and two stacked bar charts
                (inventory by state / by denomination, split Available/Allocated/Utilized)</td>
        </tr>
        <tr>
            <td>Order list</td>
            <td>Paginated bulk stamp orders (<code>bulk/maker/stamp-requests</code>) with
                status/denomination/remark/date-range filters and free-text search</td>
        </tr>
        <tr>
            <td>Order detail</td>
            <td>Check inventory availability (<code>inventory-check</code>); allocate already-uploaded stamps
                (<code>allocate-uploaded-stamps</code>); fulfil via a state/quantity-selection modal
                (<code>allocate-stamps</code>); reject with a mandatory reason (<code>reject-order</code>); view the
                customer's source Excel; export/print an allocation-history invoice (client-side, via <code>xlsx</code>)
            </td>
        </tr>
        <tr>
            <td>Upload Stamp</td>
            <td>Multi-file <strong>PDF-only</strong> upload of scanned stamps against a specific order id
                (<code>bulk/maker/scanned-stamps/upload/:orderId</code>) — not an Excel import, not in-app OCR/scanning;
                per-file success/failure feedback with automatic removal of successfully saved files from the retry list
            </td>
        </tr>
        <tr>
            <td>Inventory</td>
            <td>List of scanned-stamp inventory (<code>bulk/maker/scanned-stamps</code>); a detail view lets the maker
                <strong>Approve</strong>, <strong>Deface</strong>, or <strong>Delete</strong> a single item — each
                action only enabled while the item's status is <code>NEW</code>
            </td>
        </tr>
        <tr>
            <td>Settings</td>
            <td>Profile view/edit + change password (forces re-login after a successful password change)</td>
        </tr>
    </table>
    <p><strong>Maker order-processing sequence (as wired in code):</strong> open order → check inventory → allocate
        uploaded stamps or fulfil via the modal → (optionally) reject with a reason instead → jump to Upload Stamp to
        add more scanned PDFs → export the allocation history as an invoice.</p>

    <h3>12.3 Checker Module — Partially Wired (confirmed gap)</h3>
    <p class="gap"><strong>The Checker role exists and can log in, but cannot currently act on any order.</strong>
        Specifics, all confirmed from source:</p>
    <ul>
        <li>The Checker dashboard and order list are fully functional and read real data
            (<code>bulk/checker/dashboard</code>, <code>bulk/checker/stamp-requests</code>), scoped correctly to the
            checker role.</li>
        <li>Both Maker's and Checker's order-list screens reuse the <strong>same</strong> shared
            <code>ClientOrderComponent</code>. Its "view" action <em>unconditionally</em> navigates to
            <code>maker/order/&#123;id&#125;</code> — a route that only exists under <code>/maker</code> and is guarded
            to require
            role <code>MAKER</code>. A Checker clicking "view" is therefore bounced straight back to
            <code>/checker/dashboard</code> by the route guard.
        </li>
        <li>The order-detail component itself, <code>BulkOrderDetailComponent</code>, hardcodes
            <code>this.isMaker = true</code> in <code>ngOnInit()</code> regardless of the actual logged-in role or route
            — so even if a Checker could reach it, it would render and behave as the Maker view.
        </li>
        <li>The backend-facing pieces of a two-person approval flow <em>do</em> exist in code —
            <code>OrderService.updateStatus()</code> (<code>POST &#123;role&#125;/verify</code>, with an
            <code>action: 'APPROVE'</code>/other payload) and <code>OrderService.checkerRemark()</code>
            (<code>POST checker/order/:orderId/remark</code>) — but a repository-wide search found <strong>no component
                anywhere that calls either method.</strong> They are currently dead/orphaned code from the Checker's
            perspective.
        </li>
        <li><code>CheckerProfileComponent</code> is an unimplemented placeholder (empty class, literal
            <code>&lt;p&gt;Checker Profile works!&lt;/p&gt;</code> template).
        </li>
        <li><code>CheckerSettingsComponent</code> wraps the Maker's settings component; its
            profile-update/password-change calls are misrouted to Maker endpoints (§5.3).</li>
    </ul>
    <p><strong>Net effect:</strong> despite the status vocabulary implying a two-person Maker→Checker approval workflow
        (<code>UNDER_CHECKER_REVIEW</code>), only the Maker role can currently take any action on an order through this
        application. This is a business-relevant gap worth prioritizing if the two-person control is a compliance
        requirement.</p>

    <h3>12.4 Access Control</h3>
    <p class="gap">Within the Admin app, no Angular route guard, <code>*ngIf</code> role check, or JWT-role branching
        gates access to the Approve/Reject/Verify/Generate-Invoice/Assign-Maker actions — any authenticated Admin user
        reaching these routes can perform all of them. Within the Maker/Checker app, role separation is enforced only at
        the top-level route (<code>authGuard</code> comparing <code>route.data.role</code>) — there is no finer-grained,
        action-level permission check in either app.</p>

    <h3>12.5 Scaffolded but Inactive Code (Admin app)</h3>
    <p>Two features are partially built but <strong>disabled/dead</strong> in Admin's <code>UserComponent</code>: (1)
        per-state approval (checkbox list bound to <code>selectedStateIds</code>, calling <code>getStates(role)</code>)
        — the template block is fully commented out; (2) active/inactive toggle (<code>toggleUserStatus</code>) — the
        actual API call is commented out, only a native <code>confirm()</code> remains.</p>

    <h2 id="s13">13. Orders and Inventory</h2>

    <h3>13.1 Order Creation</h3>
    <table>
        <tr>
            <th>Order type</th>
            <th>Created by</th>
            <th>Entry point</th>
        </tr>
        <tr>
            <td>Subscription/plan order</td>
            <td>Admin (on behalf of tenant) or Customer (self-service upgrade)</td>
            <td>Admin: <code>DetailComponent.CreatePlan()</code> → <code>POST /orders</code>. Customer:
                <code>BillingComponent</code> upgrade flow → <code>POST /orders</code> (via
                <code>BillingService.createPlan</code>)
            </td>
        </tr>
        <tr>
            <td>Single e-stamp order</td>
            <td>Customer</td>
            <td><code>CreateCustomerOrderComponent</code> → <code>POST /bulk-estamp/order</code></td>
        </tr>
        <tr>
            <td>Bulk e-stamp order</td>
            <td>Customer</td>
            <td><code>UploadStampExcel</code> → <code>POST /bulk-estamp/stamp-requests/upload</code></td>
        </tr>
    </table>
    <p>Both plan-order flows generate a <strong>client-side pseudo transaction ID</strong> (<code>TXN-</code> + base36
        timestamp + random chars via <code>crypto.getRandomValues</code>) — no real payment-gateway integration is
        present in the frontend; the only enabled payment method in both apps' UI is <code>COD</code> (Online is present
        as an option but disabled).</p>

    <h3>13.2 Order Listing, Search, Filters, Pagination</h3>
    <p>Every order/document/inventory list in both apps follows the same pattern: a PrimeNG <code>p-table</code> with
        <code>[lazy]="true"</code> server-side pagination, fed by the shared <code>FilterComponent</code> (search text,
        status dropdown, date range) emitting a <code>filterChange</code>/<code>onFilterChange</code> event that
        rebuilds query params (<code>page, limit, search, status, startDate, endDate</code>) and calls the relevant
        service. Server responses commonly return <code>&#123; data, meta: &#123; total &#125; &#125;</code> or
        <code>&#123; data, totalCount &#125;</code>, mapped to the paginator's <code>totalRecords</code>.
    </p>

    <h3>13.3 Order Details</h3>
    <p>Admin's <code>ClientOrderDetailComponent</code> (bulk e-stamp order detail) is fetched purely from Angular Router
        navigation <code>state</code> passed by the list component — <strong>it performs no independent API call by
            route <code>:id</code></strong>, so a hard refresh or direct link yields an empty view (see §30).
        Customer-side detail views (<code>getStampOrderDetail</code>, <code>getBulkOrderDetail</code>) do fetch by ID
        from the API.</p>

    <h3>13.4 Order Status Flow (bulk e-stamp, as evidenced by UI-wired transitions)</h3>
    <pre>ORDER_CREATED
   │  (Admin: Generate Invoice)
   ▼
INVOICE_GENERATED
   │  (Customer: upload payment proof)
   ▼
PAYMENT_SUBMITTED ──(Admin: Reject)──► PAYMENT_REJECTED
   │  (Admin: Verify)
   ▼
ORDER_PROCESSING
   │  (server-side fulfilment — no admin UI action found)
   ▼
PARTIAL_FULFILLED / ORDER_FULFILLED   (or ORDER_REJECTED)
   │
   ▼  (continued in the Maker/Checker app, same bsrId)
Maker: inventory-check → allocate-uploaded-stamps / allocate-stamps (fulfil) → reject-order (off-ramp)
Checker: intended UNDER_CHECKER_REVIEW approval step — not reachable via the current UI (§12.3)</pre>
    <p class="note">This diagram reflects only the transitions with a corresponding UI action in the frontend code; the
        full state machine (including <code>UNDER_CHECKER_REVIEW</code>, <code>ESTAMP_UPLOADED</code>) is implemented
        server-side and not fully visible from the frontend alone. The Maker/Checker app's own
        <code>ClientOrderComponent</code> filter list matches Admin/Customer's almost exactly
        (<code>ORDER_CREATED, ORDER_REJECTED, INVOICE_GENERATED, PAYMENT_SUBMITTED, PAYMENT_REJECTED, ORDER_PROCESSING, PARTIAL_FULFILLED, ORDER_FULFILLED</code>),
        confirming all three apps share one status vocabulary.
    </p>

    <h3>13.5 Inventory</h3>
    <p>Inventory ("scanned stamps") is <strong>read-only from Admin and Customer's perspective</strong> — listing,
        filtering, and PDF preview only. Admin: <code>GET /bulk-estamp/admin/scanned-stamps</code>. Customer:
        <code>GET /bulk-estamp/client/scanned-stamps</code>.
    </p>
    <p>The <strong>Maker/Checker app is where inventory is actually created and managed</strong>: a Maker uploads
        scanned stamp PDFs (<code>bulk/maker/scanned-stamps/upload/:orderId</code>), which become inventory items
        starting at status <code>NEW</code>; the Maker can then <strong>Approve</strong>
        (<code>maker/stamp-approve/:id</code>), <strong>Deface</strong> (<code>maker/stamp-deface/:id</code>), or
        <strong>Delete</strong> (<code>maker/stamp-inventory/:id</code>) an item while it remains <code>NEW</code>. Once
        approved, items presumably become <code>AVAILABLE</code> and progress through <code>ALLOCATED → UTILIZED</code>
        — the same status labels Admin/Customer display read-only. The <code>InventoryItem</code> model file in the
        Maker app additionally types a fourth vocabulary (<code>AVAILABLE | ASSIGNED | STAMPED | DEFACED</code>) that
        doesn't fully reconcile with the dashboard/filter status set used elsewhere in the same app — an inconsistency
        present in the source, not a documentation error.
    </p>

    <h2 id="s14">14. Common and Reusable Components</h2>

    <table>
        <tr>
            <th>Component</th>
            <th>App(s)</th>
            <th>Purpose</th>
            <th>Inputs</th>
            <th>Outputs</th>
            <th>Reused in</th>
        </tr>
        <tr>
            <td><code>FilterComponent</code> (<code>app-filter</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span> <span class="app-tag customer-tag">Customer</span></td>
            <td>Search/status/plan/date-range filter toolbar above list tables; debounced search (500ms)</td>
            <td><code>statusOptions</code>, <code>planOptions</code>, <code>isOrder</code>, <code>isDateRange</code>,
                <code>searchBY</code>, <code>filter</code> (Admin) / <code>searchPlaceholder</code>,
                <code>initialStatus</code> (Customer)
            </td>
            <td><code>filterChange</code></td>
            <td>10+ list/table pages in both apps</td>
        </tr>
        <tr>
            <td><code>PaginationComponent</code> (<code>app-pagination</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span></td>
            <td>Custom Bootstrap pager</td>
            <td><code>currentPage</code>, <code>totalPages</code>, <code>pageSize</code></td>
            <td><code>pageChange</code>, <code>pageSizeChange</code></td>
            <td class="gap"><strong>Declared/exported but never used in any template</strong> — all lists use PrimeNG's
                built-in paginator instead (dead code)</td>
        </tr>
        <tr>
            <td><code>ToastComponent</code> (<code>app-toast</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span> <span class="app-tag customer-tag">Customer</span></td>
            <td>Global single-slot toast notification, driven by <code>ToastService</code></td>
            <td>none (singleton)</td>
            <td>none</td>
            <td>Mounted once in <code>app.component.html</code></td>
        </tr>
        <tr>
            <td><code>ValidationMessageComponent</code> (<code>app-validation-message</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span></td>
            <td>Dismissible list of validation error strings</td>
            <td><code>messages[]</code>, <code>title</code>, <code>dismissible</code></td>
            <td><code>dismissed</code></td>
            <td><code>add-template</code></td>
        </tr>
        <tr>
            <td><code>ApiValidationMessageComponent</code> (<code>app-api-validation-message</code>)</td>
            <td><span class="app-tag customer-tag">Customer</span></td>
            <td>Inline API-error banner (message + per-field errors), paired with
                <code>normalizeApiValidationError()</code>
            </td>
            <td><code>error</code>, <code>title</code>, <code>dismissible</code></td>
            <td><code>dismissed</code></td>
            <td><code>upload-stamp-excel</code>, <code>create-customer-order</code>, <code>send-document</code>,
                <code>add-template</code>
            </td>
        </tr>
        <tr>
            <td><code>ConfirmModalComponent</code> (<code>app-confirm-modal</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span> <span class="app-tag customer-tag">Customer</span></td>
            <td>Global "Are you sure?" confirmation dialog, driven by <code>ConfirmModalService</code> (Subject-based)
            </td>
            <td>none (singleton)</td>
            <td>none</td>
            <td>Mounted once globally; used before most destructive/critical actions (inconsistently — see §30)</td>
        </tr>
        <tr>
            <td><code>LoaderComponent</code> (<code>app-loader</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span> <span class="app-tag customer-tag">Customer</span></td>
            <td>Full-block or inline button spinner</td>
            <td><code>loading</code>, <code>buttonLoader</code></td>
            <td>none</td>
            <td>13-15 pages in each app</td>
        </tr>
        <tr>
            <td><code>PagetitleComponent</code> (<code>app-pagetitle</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span> <span class="app-tag customer-tag">Customer</span></td>
            <td>Page header + breadcrumb + optional action button</td>
            <td><code>title</code>, <code>breadcrumbItems</code>, <code>actionButton</code></td>
            <td>none</td>
            <td>Nearly every page component</td>
        </tr>
        <tr>
            <td><code>StatComponent</code> (<code>app-stat</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span> <span class="app-tag customer-tag">Customer</span></td>
            <td>Dashboard KPI/stat tile, clickable to navigate</td>
            <td>Admin: <code>statData</code>, <code>title</code>, <code>value</code>, <code>icon</code>,
                <code>navigateUrl</code>; Customer: <code>documentsCount</code>, <code>planInfo</code>
            </td>
            <td>none</td>
            <td>Analytics/dashboard pages. <span class="gap">Admin's multi-item template has a data-binding bug (see
                    §30).</span></td>
        </tr>
        <tr>
            <td><code>PdfSignerComponent</code> (<code>app-pdf-signer</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span> <span class="app-tag customer-tag">Customer</span></td>
            <td>Interactive signature-box placement on a rendered PDF</td>
            <td><code>signData</code></td>
            <td><code>close</code></td>
            <td>Send-document, add-template (both apps)</td>
        </tr>
        <tr>
            <td><code>ViewPdfComponent</code> (<code>app-view-pdf</code>)</td>
            <td><span class="app-tag admin-tag">Admin</span> <span class="app-tag customer-tag">Customer</span></td>
            <td>Read-only PDF preview with all signer positions burned in, plus raw JSON viewer</td>
            <td><code>documentData</code></td>
            <td><code>close</code></td>
            <td>Templates "view" action</td>
        </tr>
        <tr>
            <td><code>AuditTrailComponent</code></td>
            <td><span class="app-tag customer-tag">Customer</span></td>
            <td>Signed-document audit/certificate-of-completion view</td>
            <td>—</td>
            <td>—</td>
            <td class="gap">Backed by hardcoded mock data, not a live API (see §30)</td>
        </tr>
        <tr>
            <td><code>DocumentataionComponent</code> (<code>app-documentataion</code>)</td>
            <td><span class="app-tag customer-tag">Customer</span></td>
            <td>API documentation viewer with cURL/Postman export (jsPDF-generated)</td>
            <td><code>activeTab</code></td>
            <td>—</td>
            <td><code>api-integration</code> page</td>
        </tr>
    </table>

    <h3>14.1 Maker/Checker App — Shared Components</h3>
    <p>Built independently of Admin/Customer's shared library (Angular Material based, not Bootstrap/PrimeNG), but
        serving the equivalent purposes:</p>
    <table>
        <tr>
            <th>Component</th>
            <th>Purpose</th>
            <th>Notes</th>
        </tr>
        <tr>
            <td><code>ClientOrderComponent</code></td>
            <td>Bulk-order list, reused by both Maker's and Checker's <code>OrderComponent</code> wrappers</td>
            <td class="gap">Its "view" action always routes to the Maker-only detail page — root cause of the Checker
                access gap (§12.3)</td>
        </tr>
        <tr>
            <td><code>BulkOrderDetailComponent</code></td>
            <td>Order-detail screen with all Maker actions (allocate, check inventory, reject, view Excel, export
                history)</td>
            <td class="gap">Hardcodes <code>isMaker = true</code> regardless of caller</td>
        </tr>
        <tr>
            <td><code>InventoryCheckModalComponent</code></td>
            <td>Dialog for selecting state/quantity to fulfil an order</td>
            <td>Calls <code>fulfillBulkOrder</code></td>
        </tr>
        <tr>
            <td><code>CommonTableComponent</code></td>
            <td>Generic Material data table with pagination, filter bar, status-badge styling</td>
            <td>Equivalent to Admin/Customer's PrimeNG <code>p-table</code> convention</td>
        </tr>
        <tr>
            <td><code>CommonHeaderComponent</code></td>
            <td>Reusable page header with title/back/add button + select dropdown</td>
            <td>Equivalent to <code>PagetitleComponent</code></td>
        </tr>
        <tr>
            <td><code>ConfirmationDialogComponent</code> / <code>ConfirmationDialogService</code></td>
            <td>Yes/No confirmation, optionally with a required text input (used for reject-order reasons)</td>
            <td>Equivalent to <code>ConfirmModalService</code></td>
        </tr>
        <tr>
            <td><code>ToasterComponent</code> / <code>ToasterService</code></td>
            <td>Wraps Angular Material's <code>MatSnackBar</code></td>
            <td>Equivalent to <code>ToastService</code></td>
        </tr>
        <tr>
            <td><code>LoaderComponent</code></td>
            <td>Full-block or inline spinner, wraps <code>MatProgressSpinnerModule</code></td>
            <td>Equivalent to <code>app-loader</code></td>
        </tr>
        <tr>
            <td><code>SidebarComponent</code></td>
            <td>Role-aware nav menu — shows different links for Maker vs Checker based on
                <code>AuthService.currentRole()</code>
            </td>
            <td>Some Maker links (Stamp Upload, PDF Text Extractor) are currently commented out</td>
        </tr>
        <tr>
            <td><code>ValidationMessageComponent</code></td>
            <td>Dismissible result-summary banner for bulk operations (saved/failed counts + failed-item list)</td>
            <td>Used by Upload Stamp and order allocation</td>
        </tr>
        <tr>
            <td><code>pdf-text-extractor</code></td>
            <td>Draws regions on a rendered PDF (via <code>pdfjs-dist</code>) and posts the file + region coordinates to
                an <strong>external</strong> API (<code>extract-api.avsstech.in/extract-text</code>, independent of
                <code>environment.apiUrl</code>) to extract text per region
            </td>
            <td>See §20.5 — this is the app's only OCR-adjacent capability, and it's currently unreachable from the
                sidebar</td>
        </tr>
    </table>

    <h2 id="s15">15. Forms and Validation</h2>

    <h3>15.1 Form Technology</h3>
    <p>Both apps use Angular <strong>Reactive Forms</strong> exclusively for data-entry screens
        (<code>FormBuilder</code>/<code>FormGroup</code>/<code>FormArray</code>); template-driven forms are not used for
        business forms (only trivial template bindings elsewhere).</p>

    <h3>15.2 Representative Validation Rules Found</h3>
    <table>
        <tr>
            <th>Form</th>
            <th>App</th>
            <th>Rules</th>
        </tr>
        <tr>
            <td>Login</td>
            <td>Both</td>
            <td>Email format + required; OTP 6-digit pattern</td>
        </tr>
        <tr>
            <td>Signup</td>
            <td>Both</td>
            <td>Username/email/password required; Customer additionally: phone Indian-mobile pattern
                <code>^[6-9][0-9]&#123;9&#125;$</code>, company field, password confirmation
            </td>
        </tr>
        <tr>
            <td>Password reset</td>
            <td>Both</td>
            <td>New-password pattern requiring letter+digit+special char, min 8 chars; custom cross-field
                <code>passwordMatchValidator</code> comparing password/confirm
            </td>
        </tr>
        <tr>
            <td>Send Document — signer</td>
            <td>Customer</td>
            <td>Name required; email required + format + custom domain-duplication validator; phone Indian-mobile
                pattern; <code>esignType</code> required; at least one signature position required per signer (custom
                cross-component check against in-memory signer store)</td>
        </tr>
        <tr>
            <td>Add Template</td>
            <td>Both</td>
            <td>Name/category required; file-type check (PDF); at least one signer; each signer must have a placed
                signature position</td>
        </tr>
        <tr>
            <td>Create Customer Order (e-stamp)</td>
            <td>Customer</td>
            <td>State/denomination/purpose/parties required; file must be PDF, ≤10MB; keystroke-level input filtering
                (letters-only / digits-only fields)</td>
        </tr>
        <tr>
            <td>Upload Stamp Excel</td>
            <td>Customer</td>
            <td>See §11.4 (file type/size + parsed-content validation)</td>
        </tr>
        <tr>
            <td>S3 Setup</td>
            <td>Customer</td>
            <td>All 5 fields required + non-whitespace pattern</td>
        </tr>
        <tr>
            <td>Plans</td>
            <td>Admin</td>
            <td>Name required; price/documents/users required + min/max range + integer pattern; features capped at 10
                entries</td>
        </tr>
        <tr>
            <td>Settings — Platform</td>
            <td>Admin</td>
            <td>Support email format + custom validator blocking repeated-TLD addresses (e.g. <code>.com.com</code>)
            </td>
        </tr>
        <tr>
            <td>Settings — Security</td>
            <td>Admin</td>
            <td>Session timeout required, max 1440 (no minimum enforced — negative values pass)</td>
        </tr>
        <tr>
            <td>Cron Job / State / Vendor CRUD</td>
            <td>Admin</td>
            <td>Standard required-field validation per form</td>
        </tr>
        <tr>
            <td>E-Stamp Customer Config</td>
            <td>Admin</td>
            <td>Email-recipient list: per-address regex validation + case-insensitive de-duplication; conditionally
                required numeric field depending on selected loss-policy mode</td>
        </tr>
    </table>

    <h3>15.3 Submit Handling Pattern</h3>
    <p>Consistent pattern across both apps: guard against double-submit via a <code>loading</code> boolean → validate
        the form/business rules → (for destructive or significant actions) open <code>ConfirmModalService</code> → call
        the service → on success, reset the form/reload the list/show a success toast; on error, either fall through to
        the global error toast or set an inline <code>apiValidationError</code>/<code>validationSummary</code> object
        for display via the validation-message components (§14).</p>

    <h2 id="s16">16. HTTP Interceptors</h2>

    <h3>16.1 Admin App</h3>
    <p>Registered in <code>app.module.ts</code>, executed in this order: <strong>JwtInterceptor → ErrorInterceptor →
            FakeBackendInterceptor</strong>.</p>
    <table>
        <tr>
            <th>Interceptor</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td><code>JwtInterceptor</code></td>
            <td>Reads <code>AuthenticationService.currentUser()</code> and, if a token exists, clones the request adding
                <code>Authorization: Bearer &lt;token&gt;</code>. Applied to every outgoing request, no URL filtering.
            </td>
        </tr>
        <tr>
            <td><code>ErrorInterceptor</code></td>
            <td>Catches every HTTP error; normalizes status/message into a <code>NormalizedHttpError</code>; shows a
                status-specific toast via <code>ToastService</code> (resolved through <code>Injector</code> to avoid
                circular DI); on HTTP <code>401</code> also calls <code>AuthenticationService.logout()</code>.</td>
        </tr>
        <tr>
            <td><code>FakeBackendInterceptor</code></td>
            <td>Legacy in-memory mock backend (from the original admin template) intercepting <code>/users*</code> paths
                only — effectively inert since no real service calls those paths; all other requests pass through
                untouched.</td>
        </tr>
    </table>

    <h3>16.2 Customer App</h3>
    <p>Registered in <code>app.module.ts</code>, executed in this order: <strong>JwtInterceptor → ErrorInterceptor →
            EnvironmentInterceptor</strong>.</p>
    <table>
        <tr>
            <th>Interceptor</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td><code>JwtInterceptor</code></td>
            <td>Attaches <code>Authorization: Bearer &lt;access_token&gt;</code> from
                <code>AuthfakeauthenticationService.currentUserValue</code> (falls back to Firebase path only if
                <code>defaultauth==='firebase'</code>).
            </td>
        </tr>
        <tr>
            <td><code>ErrorInterceptor</code></td>
            <td>Same normalization pattern as Admin, plus an opt-out mechanism: skips the global toast when the
                request's <code>HttpContext</code> carries <code>SKIP_GLOBAL_ERROR_TOAST = true</code> (used by
                document/template/S3/e-stamp-order creation calls that show inline errors instead). Does
                <strong>not</strong> auto-logout on 401 — only shows a "session expired" toast.
            </td>
        </tr>
        <tr>
            <td><code>EnvironmentInterceptor</code></td>
            <td>Prepends the correct base URL to every relative request (skips URLs starting with <code>assets</code>):
                <code>environment.apiUrl</code> by default, or <code>environment.otherUrl</code> if the request carries
                header <code>X-API-TYPE: OTHER</code> (which it then strips before sending). This is what allows every
                service to use bare relative paths like <code>/documents</code>.
            </td>
        </tr>
    </table>
    <p><strong>Effective request-side order</strong> (interceptors run outgoing requests in registration order): Jwt
        (adds header) → Error (pass-through wrapper) → Environment (prefixes URL last). Responses/errors unwind in
        reverse.</p>

    <h3>16.3 Maker/Checker App</h3>
    <p>Uses the newer <strong>functional</strong> interceptor style (<code>HttpInterceptorFn</code>) registered via
        <code>provideHttpClient(withInterceptors([...]))</code> in <code>app.config.ts</code>, executed in this order:
        <strong>apiBaseUrlInterceptor → authInterceptor → errorInterceptor</strong>.
    </p>
    <table>
        <tr>
            <th>Interceptor</th>
            <th>Purpose</th>
        </tr>
        <tr>
            <td><code>apiBaseUrlInterceptor</code></td>
            <td>Prefixes any relative request URL with <code>environment.apiUrl</code>; passes absolute
                (<code>http...</code>) URLs through unchanged — runs <strong>first</strong>, unlike Customer's
                equivalent which runs last.</td>
        </tr>
        <tr>
            <td><code>authInterceptor</code></td>
            <td>Attaches <code>Authorization: Bearer &lt;accessToken&gt;</code> from the stored session.</td>
        </tr>
        <tr>
            <td><code>errorInterceptor</code></td>
            <td class="gap">A trivial pass-through — <code>catchError</code> that just rethrows the error. <strong>No
                    centralized toast, 401 handling, or logout logic exists here at all</strong> — every component is
                individually responsible for calling <code>ToasterService</code> on failure.</td>
        </tr>
    </table>

    <h2 id="s17">17. Route Guards</h2>
    <table>
        <tr>
            <th>Guard</th>
            <th>App</th>
            <th>Type</th>
            <th>Protects</th>
            <th>Logic</th>
            <th>Redirect on failure</th>
        </tr>
        <tr>
            <td><code>AuthGuard</code></td>
            <td>Admin</td>
            <td><code>canActivate</code> (class)</td>
            <td>Root <code>''</code> route (entire authenticated shell)</td>
            <td><code>currentUser?.role === 'admin'</code></td>
            <td><code>/account/login</code> with <code>returnUrl</code> query param</td>
        </tr>
        <tr>
            <td><code>AuthGuard</code></td>
            <td>Customer</td>
            <td><code>canActivate</code> (class)</td>
            <td><code>/customer</code> route (entire authenticated shell)</td>
            <td>Firebase mode: any authenticated user; default mode: <code>currentUser.user.role === 'customer'</code>
            </td>
            <td><code>/account/login</code> with <code>returnUrl</code></td>
        </tr>
        <tr>
            <td><code>eStampActiveGuard</code></td>
            <td>Customer</td>
            <td><code>CanMatchFn</code> (functional)</td>
            <td class="gap">Imported but <strong>never attached to any route</strong> — currently inert at the routing
                layer</td>
            <td>Checks <code>AccountService.getEstampCustomerProfile()</code>; allows if
                <code>estampService === 'ACTIVE'</code>
            </td>
            <td>Would redirect to <code>/customer/account?profile=estamp</code> with a toast, if it were wired</td>
        </tr>
        <tr>
            <td><code>authGuard</code></td>
            <td>Maker/Checker</td>
            <td><code>CanActivateFn</code> (functional)</td>
            <td><code>/maker</code> and <code>/checker</code> route trees, each with <code>data.role</code></td>
            <td>No session → redirect to login. Session present but <code>currentUser.user.role</code> doesn't match the
                route's declared role → redirect to the <em>other</em> role's dashboard (not login)</td>
            <td><code>/auth/login</code> (no session) or <code>/checker/dashboard</code>/<code>/maker/dashboard</code>
                (role mismatch)</td>
        </tr>
        <tr>
            <td><code>guestGuard</code></td>
            <td>Maker/Checker</td>
            <td><code>CanActivateFn</code> (functional)</td>
            <td class="gap">Wired onto <code>/auth</code> but its body is hardcoded <code>return true;</code> — performs
                no real check</td>
            <td>None (no-op)</td>
            <td>N/A</td>
        </tr>
    </table>
    <p class="gap">E-Stamp module access in the Customer app is enforced only at the UI level —
        <code>SidebarComponent.onParentClick()</code> intercepts clicks on the E-Stamp menu item and blocks navigation
        client-side if the tenant's e-stamp status isn't <code>ACTIVE</code>. A user who navigates directly to
        <code>/customer/estamp/...</code> by URL bypasses this check entirely (the route itself has no guard).
        Similarly, in the Maker/Checker app, no route exists for <code>/checker/order/:id</code> at all, so a Checker
        cannot reach an order-detail page by any means, direct URL or otherwise (§12.3).
    </p>

    <h2 id="s18">18. Data Flow</h2>

    <h3>18.1 Request/Response Flow</h3>
    <pre>Component (template event)
   → Component method
   → injected core/services/*.service.ts method (HttpClient.get/post/patch/delete)
   → [Admin] JwtInterceptor → ErrorInterceptor → FakeBackendInterceptor
   → [Customer] JwtInterceptor → ErrorInterceptor → EnvironmentInterceptor
   → Backend API (environment.apiUrl)
   ← Response (or normalized error thrown back up the interceptor chain)
   ← Service maps response (e.g. adds totalCount from res.meta.total)
   ← Component subscribe() updates local component state
   ← Template re-renders (PrimeNG table, form, etc.)</pre>

    <h3>18.2 Component Communication</h3>
    <ul>
        <li><strong>Parent → Child:</strong> <code>&#64;Input()</code> bindings (e.g. <code>AddTemplateComponent</code>
            receives <code>&#64;Input() template</code> from <code>TemplatesComponent</code> for edit mode;
            <code>PdfSignerComponent</code> receives <code>&#64;Input() signData</code>).
        </li>
        <li><strong>Child → Parent:</strong> <code>&#64;Output() EventEmitter</code> (e.g.
            <code>PdfSignerComponent.close</code> emits signature-position data back to the caller;
            <code>FilterComponent.filterChange</code> emits filter criteria).
        </li>
        <li><strong>Cross-step / cross-component scratch state (no HTTP):</strong> <code>DocumentService</code> in both
            apps exposes an in-memory <code>getSigners</code>/<code>setSigners</code> accessor pair backed by a private
            array — used to pass signer + signature-position data between the multi-step send-document wizard and the
            <code>PdfSignerComponent</code> overlay without round-tripping to the backend.
        </li>
        <li><strong>Global singletons (Subject/BehaviorSubject-driven):</strong> <code>ToastService</code> (toast
            notifications), <code>ConfirmModalService</code> (confirmation dialogs), <code>EventService</code> (generic
            pub/sub, used for layout-type switching between vertical/horizontal).</li>
    </ul>

    <h3>18.3 Storage</h3>
    <table>
        <tr>
            <th>Storage</th>
            <th>Usage</th>
        </tr>
        <tr>
            <td><code>sessionStorage</code></td>
            <td>The <strong>only</strong> place authentication session data is stored (key <code>authUser</code> in
                Admin, <code>currentUser</code> in Customer) — cleared on logout or browser/tab close.</td>
        </tr>
        <tr>
            <td><code>localStorage</code></td>
            <td>Used only by the legacy/dormant <code>AuthfakeauthenticationService</code> (Admin) fake-backend
                companion — not part of the real session in either app's active auth path.</td>
        </tr>
        <tr>
            <td>Cookies</td>
            <td>Used only for the <code>lang</code> language-preference cookie via <code>ngx-cookie-service</code>
                (<code>LanguageService</code>).</td>
        </tr>
        <tr>
            <td>Router navigation <code>state</code></td>
            <td>Used to pass a full object (e.g. an order row) from a list screen to a detail screen without a fresh API
                fetch — see the <code>ClientOrderDetailComponent</code> gap noted in §13.3/§30.</td>
        </tr>
    </table>

    <h2 id="s19">19. State Management</h2>
    <p>Neither app uses NgRx, Akita, Signals, or any dedicated state-management library. State is managed through:</p>
    <ul>
        <li><strong>Services as state holders</strong> — every domain service is a <code>providedIn:'root'</code>
            singleton; some (e.g. <code>DocumentService</code>) hold private in-memory arrays as a scratch store for
            multi-step flows.</li>
        <li><strong><code>BehaviorSubject</code>/<code>Subject</code>-based reactive singletons</strong> for
            cross-cutting UI concerns: <code>ToastService.toast$</code> (single-slot toast),
            <code>ConfirmModalService.modalState</code> (confirm dialog),
            <code>AuthfakeauthenticationService.currentUserSubject</code> (current session), <code>EventService</code>
            (generic broadcast bus for layout events).
        </li>
        <li><strong><code>sessionStorage</code></strong> as the persistence layer for the authenticated session
            (survives page reloads within a tab, not across browser restarts).</li>
        <li><strong>Component-local state</strong> for everything else (form models, list data, pagination counters) —
            no shared/global store beyond the singletons above.</li>
    </ul>

    <h2 id="s20">20. File, PDF and OCR Functionality</h2>

    <h3>20.1 File Upload</h3>
    <p>Every upload flow (documents, templates, e-stamp orders, bulk Excel, payment proof) follows the same pattern:
        native <code>&lt;input type="file"&gt;</code> change handler → client-side MIME-type + extension + size
        validation → held as a <code>File</code> object in component state → submitted as part of a
        <code>FormData</code> payload built by a small per-service <code>jsonToFormData</code>-style helper (File values
        appended as-is, objects/arrays JSON-stringified, primitives appended as strings).
    </p>
    <table>
        <tr>
            <th>Upload</th>
            <th>Allowed types</th>
            <th>Max size</th>
        </tr>
        <tr>
            <td>E-Sign document (send/template)</td>
            <td><code>application/pdf</code> (MIME or <code>.pdf</code> extension)</td>
            <td>10MB (10.1MB in send-document)</td>
        </tr>
        <tr>
            <td>E-Stamp single order document</td>
            <td><code>application/pdf</code></td>
            <td>10MB</td>
        </tr>
        <tr>
            <td>Payment proof image</td>
            <td>JPG / JPEG / PNG</td>
            <td>2MB</td>
        </tr>
        <tr>
            <td>Bulk order Excel</td>
            <td><code>.xlsx</code>, <code>.xls</code>, <code>.csv</code> (specific MIME types checked)</td>
            <td>1MB</td>
        </tr>
    </table>

    <h3>20.2 PDF Preview / Rendering</h3>
    <p><code>pdfjs-dist</code> renders every page of a PDF to an on-screen <code>&lt;canvas&gt;</code> element, used
        identically by <code>PdfSignerComponent</code> (interactive) and <code>ViewPdfComponent</code> (read-only). The
        PDF.js worker is loaded from a public CDN (<code>cdnjs.cloudflare.com/.../pdf.worker.min.js</code> v3.11.174)
        rather than being bundled — an external runtime dependency worth noting for offline/air-gapped deployments.</p>

    <h3>20.3 PDF Processing / Generation</h3>
    <p><code>pdf-lib</code> loads the original PDF's bytes and programmatically draws a rectangle + "Sign here [– signer
        name]" text at each stored signature-box coordinate, producing a new in-memory PDF (Blob/object URL) used purely
        for <strong>preview</strong> purposes — it does not perform an actual cryptographic signature application.
        Coordinates are stored in PDF-point units and converted to/from on-screen pixels using a fixed scale factor
        (<code>SCALE = 1.3</code>).</p>

    <h3>20.4 PDF Text Extraction</h3>
    <p class="na">Not available in the provided source code. No text-extraction logic (e.g. <code>pdfjs-dist</code>'s
        <code>getTextContent</code>) was found in any analyzed file.
    </p>

    <h3>20.5 OCR</h3>
    <p>Not available in the Admin or Customer apps — no OCR library (e.g. Tesseract.js) or OCR-related service/component
        exists in either.</p>
    <p>The <strong>Maker/Checker app</strong>, however, does have an OCR-adjacent capability: its standalone
        <code>pdf-text-extractor</code> component lets a user draw rectangular regions on a rendered PDF and submits the
        file + region coordinates as <code>FormData</code> to an <strong>external, third-party API</strong>
        (<code>https://extract-api.avsstech.in/extract-text</code> — hardcoded, independent of the app's own
        <code>environment.apiUrl</code>). The response includes an <code>extraction_method</code> field per region
        (<code>embedded_text | ocr | none</code>), indicating the external service itself falls back to OCR when a PDF
        has no embedded text layer — the OCR processing happens entirely server-side on that third-party endpoint, not
        in this Angular app. This tool is currently disconnected from the core stamp-upload workflow and hidden from the
        sidebar navigation.
    </p>

    <h3>20.6 Region Selection</h3>
    <p>Implemented as part of the signature-placement workflow: <code>PdfSignerComponent</code> supports
        double-click-to-place, drag (move), and resize (via a bottom-right handle, clamped 220×80 to 360×180 px) of
        rectangular "signature here" regions per PDF page, with an option to apply the same region to every page.</p>

    <h3>20.7 Download Functionality</h3>
    <ul>
        <li><strong>Signed document:</strong> <code>GET /documents/:id/download</code> returns a URL, opened directly
            (<code>window.open</code>).</li>
        <li><strong>Template file:</strong> synthetic <code>&lt;a download&gt;</code> anchor click against the
            template's stored <code>fileUrl</code>.</li>
        <li><strong>Merged e-stamp document:</strong> synthetic anchor download of <code>document.mergedFileUrl</code>.
        </li>
        <li><strong>Audit trail:</strong> real endpoint returns text/HTML opened in a new tab (not a file download); a
            separate <code>html2pdf.js</code>-based DOM-to-PDF export exists in
            <code>AuditTrailService.download()</code> but its only call site is commented out (currently inactive).
        </li>
        <li><strong>API documentation exports:</strong> <code>jsPDF</code>-generated cURL-reference PDF and a hand-built
            Postman Collection v2.1 JSON, both via Blob + synthetic anchor download
            (<code>DocumentataionComponent</code>).</li>
    </ul>

    <h3>20.8 Error Handling (file/PDF specific)</h3>
    <p>Handled entirely client-side per upload component: invalid type/oversized files are rejected with an inline error
        message and a toast, and the native file input is reset. PDF rendering failures are not explicitly
        caught/handled with a user-facing message in the reviewed code beyond generic try/catch blocks in a few submit
        handlers.</p>

    <h2 id="s21">21. Error Handling</h2>

    <h3>21.1 HTTP / API Errors</h3>
    <p>Centralized in each app's <code>ErrorInterceptor</code> (see §16). Both apps normalize errors into a consistent
        shape and map HTTP status codes to human-readable titles/messages:</p>
    <table>
        <tr>
            <th>Status</th>
            <th>Admin fallback title/message</th>
            <th>Customer fallback title/message</th>
        </tr>
        <tr>
            <td>0</td>
            <td>Connection error</td>
            <td>Connection Error</td>
        </tr>
        <tr>
            <td>400</td>
            <td>Request failed</td>
            <td>Request failed</td>
        </tr>
        <tr>
            <td>401</td>
            <td>Unauthorized (also force-logs-out)</td>
            <td>Unauthorized — "Your session has expired. Please login again." (no auto-logout)</td>
        </tr>
        <tr>
            <td>403</td>
            <td>Forbidden</td>
            <td>Forbidden</td>
        </tr>
        <tr>
            <td>404</td>
            <td>Not found</td>
            <td>Not Found</td>
        </tr>
        <tr>
            <td>409</td>
            <td>Conflict</td>
            <td>Conflict</td>
        </tr>
        <tr>
            <td>422</td>
            <td>Validation failed</td>
            <td>Validation Failed</td>
        </tr>
        <tr>
            <td>429</td>
            <td>Too many requests</td>
            <td>Too Many Requests</td>
        </tr>
        <tr>
            <td>5xx</td>
            <td>Server error</td>
            <td>Server Error</td>
        </tr>
    </table>

    <h3>21.2 Form Validation Errors</h3>
    <p>Rendered inline via each form's own error-message markup (bound to
        <code>FormControl.errors</code>/<code>touched</code> state) plus, for API-returned field errors, the dedicated
        <code>ValidationMessageComponent</code> (Admin) / <code>ApiValidationMessageComponent</code> (Customer) — see
        §14.
    </p>

    <h3>21.3 Global Error Handling</h3>
    <p class="gap">Neither app overrides Angular's built-in <code>ErrorHandler</code> — there is no global
        JavaScript-exception capture/reporting mechanism; only HTTP errors are centrally handled (via the interceptor).
        Unhandled synchronous/template errors would surface only in the browser console.</p>

    <h3>21.4 Toast/Notification Mechanism</h3>
    <p>Both apps implement an identical pattern: <code>ToastService</code> (<code>providedIn:'root'</code>) holds a
        single-slot <code>BehaviorSubject&lt;ToastMessage|null&gt;</code>;
        <code>showToaster(title, message, type)</code> pushes a toast that auto-clears after 3000ms; a single
        <code>ToastComponent</code> instance mounted once in <code>app.component.html</code> renders whatever is
        currently in the subject. Only one toast can be visible at a time — a second toast fired within the 3-second
        window replaces the first without resetting cleanly (a minor race noted during analysis).
    </p>

    <h2 id="s22">22. Testing</h2>

    <h3>22.1 Framework</h3>
    <p>Both apps use the standard Angular CLI test stack: <strong>Karma</strong> (test runner) +
        <strong>Jasmine</strong> (assertion/spec framework), configured via each app's <code>karma.conf.js</code>
        (Chrome launcher, Istanbul coverage reporter writing to <code>./coverage/nazox</code>,
        <code>autoWatch: true</code>, <code>singleRun: false</code> — configured for local interactive runs, not
        headless CI).
    </p>

    <h3>22.2 Test Coverage — Actual State</h3>
    <p class="gap"><strong>There is no functional/behavioral unit test coverage in either application.</strong> Every
        one of the ~48 (Admin) and ~50 (Customer) <code>*.spec.ts</code> files found is default Angular-CLI-generated
        boilerplate — components assert only <code>expect(component).toBeTruthy()</code> ("should create"), services
        assert only <code>expect(service).toBeTruthy()</code> ("should be created"). No test exercises a validator, an
        HTTP call (no <code>HttpTestingController</code> usage found anywhere), a guard's authorization logic, an
        interceptor's header/error behavior, or any DOM interaction.</p>
    <ul>
        <li><code>core/services/event.service.spec.ts</code> (Admin) — entirely commented out, no active test at all.
        </li>
        <li><code>app.component.spec.ts</code> (Admin) — the only spec with more than one assertion, but it checks for
            text ("nazox app is running!") that no longer matches the current template — <strong>likely a failing/stale
                test</strong>.</li>
        <li><code>pages/plans/plans.component.spec.ts</code> (Admin) uses <code>imports: [PlansComponent]</code> in its
            TestBed config — invalid for a non-standalone component declared via an NgModule, so this spec is very
            likely broken.</li>
        <li>No spec file exists at all for: Admin's <code>validation-message</code> component; Customer's
            <code>api-validation-message</code> component, nor for most Customer core services (<code>account</code>,
            <code>api-integration</code>, <code>billing</code>, <code>s3-storage</code>, <code>toast</code>,
            <code>auth</code>, <code>authfake</code>, <code>audit-trail</code>, <code>e-stamp</code>,
            <code>user</code>).
        </li>
    </ul>

    <h3>22.3 Test Commands</h3>
    <pre># From Admin/ or Customer/
npm test        # runs `ng test` (Karma, watch mode, Chrome)
    npm run lint     # runs `ng lint` (&#64;angular-eslint)
npm run e2e      # runs `protractor` (deprecated/unmaintained upstream)</pre>

    <h3>22.4 Coverage Reports</h3>
    <p>Configured (Istanbul via <code>karma-coverage-istanbul-reporter</code>, output to <code>coverage/nazox/</code>,
        html/lcovonly/text-summary) but — given the boilerplate-only spec content above — actual line/branch coverage
        would be effectively confined to "component/service instantiates without error," not meaningful business-logic
        coverage.</p>

    <h2 id="s23">23. Development Setup</h2>

    <h3>23.1 Prerequisites</h3>
    <ul>
        <li>Node.js ≥ 20.11.0 (per root <code>package.json engines</code>; Docker build uses
            <code>node:20-alpine</code>)
        </li>
        <li>npm (bundled with Node)</li>
        <li>Angular CLI — Admin requires v17.x, Customer requires v19.x (install per-project via <code>npx</code>/local
            devDependency rather than a single global version, since the two apps target different major versions)</li>
    </ul>

    <h3>23.2 Dependency Installation</h3>
    <pre># Admin app
cd Admin
npm install --legacy-peer-deps

# Customer app
cd Customer
npm install --legacy-peer-deps

# Root Express host (only needed to run the combined production server locally)
npm install</pre>
    <p class="note"><code>--legacy-peer-deps</code> is required (confirmed by the Dockerfile's build stages) — the
        dependency tree has known peer-version conflicts, notably <code>&#64;asymmetrik/ngx-leaflet&#64;^18</code>
        (Customer)
        peer-depending on <code>&#64;angular/common&#64;18</code> while Customer runs Angular 19.2.</p>

    <h3>23.3 Environment Configuration</h3>
    <p>Edit <code>src/environments/environment.ts</code> in the relevant app to point <code>apiUrl</code> (and, for
        Customer, <code>esignAuditUrl</code>/<code>otherUrl</code>) at the desired backend. No <code>.env</code> file
        mechanism is used by the Angular apps themselves — configuration is compiled in at build time via the
        environment files (see §7).</p>

    <h3>23.4 Running Locally</h3>
    <pre># Admin — serves on the Angular CLI default port (4200) unless configured otherwise
cd Admin &amp;&amp; npm start        # ng serve

# Customer
cd Customer &amp;&amp; npm start     # ng serve</pre>
    <p class="note">Both apps default to Angular CLI's dev server on the same port (4200) — run them one at a time
        locally, or configure a different <code>--port</code> for one of them, since they cannot both bind the default
        port simultaneously.</p>

    <h3>23.5 Running Tests</h3>
    <pre>cd Admin &amp;&amp; npm test      # or: cd Customer &amp;&amp; npm test</pre>

    <h3>23.6 Building</h3>
    <pre>cd Admin &amp;&amp; npm run build                              # dev/unoptimized build
cd Admin &amp;&amp; npx ng build --configuration production      # production build (used by Docker)

cd Customer &amp;&amp; npm run build
cd Customer &amp;&amp; npx ng build --configuration production</pre>

    <h2 id="s24">24. Build and Deployment</h2>

    <h3>24.1 Build Outputs</h3>
    <p>Both apps build to <code>dist/nazox</code> (per <code>angular.json</code> <code>outputPath</code> — the legacy
        project name was never updated) using the classic <code>&#64;angular-devkit/build-angular:browser</code> builder
        (not the newer esbuild "application" builder). Output layout can therefore be either
        <code>dist/nazox/index.html</code> directly or <code>dist/nazox/browser/index.html</code> depending on Angular
        CLI version behavior — both the Dockerfile and <code>server.js</code> explicitly handle both possible layouts.
    </p>

    <h3>24.2 Development vs Production Build</h3>
    <table>
        <tr>
            <th></th>
            <th><code>ng build</code> (default)</th>
            <th><code>ng build --configuration production</code></th>
        </tr>
        <tr>
            <td>Environment file used</td>
            <td><code>environment.ts</code></td>
            <td><code>environment.prod.ts</code> (via <code>fileReplacements</code>)</td>
        </tr>
        <tr>
            <td>Optimization / minification</td>
            <td>off</td>
            <td>on</td>
        </tr>
        <tr>
            <td>Source maps</td>
            <td>on</td>
            <td>off</td>
        </tr>
        <tr>
            <td>Output hashing</td>
            <td>off</td>
            <td>on (<code>all</code> — enables long-lived cache headers)</td>
        </tr>
    </table>

    <h3>24.3 Docker Multi-Stage Build</h3>
    <pre>Stage 1 (admin-build):    node:20-alpine → npm ci --legacy-peer-deps → ng build --configuration production (Admin)
                            → normalize output → /out
Stage 2 (customer-build): same pattern for Customer/
Stage 3 (runtime):        node:20-alpine → npm ci --omit=dev (root package.json: express/helmet/compression)
                            → copy server.js
                            → COPY --from=admin-build /out    ./dist/admin
                            → COPY --from=customer-build /out ./dist/customer
                            → runs as non-root user "appuser"
                            → EXPOSE 3110, HEALTHCHECK GET /health every 30s
                            → CMD node server.js</pre>

    <h3>24.4 Runtime Serving Model</h3>
    <p>A single Express process (<code>server.js</code>) serves <strong>both</strong> built apps from one container,
        routed purely by the incoming <code>Host</code> HTTP header (no path-based routing) — e.g.
        <code>admin.example.com</code> → <code>dist/admin</code>, <code>customer.example.com</code> →
        <code>dist/customer</code>. TLS termination is expected upstream (the app only speaks plain HTTP;
        <code>trust proxy</code> is set to trust exactly one upstream hop). Each app gets standard SPA fallback
        (unmatched GET/HEAD routes serve that app's <code>index.html</code>) and differentiated cache headers:
        <code>index.html</code> is <code>no-cache</code>; hashed static assets (js/css/fonts/images matching Angular's
        <code>outputHashing</code> pattern) get a 1-year immutable cache; everything else gets a 1-hour cache.
    </p>

    <h3>24.5 Orchestration</h3>
    <pre>docker-compose up -d --build</pre>
    <p><code>docker-compose.yml</code> defines one service (<code>angular-apps</code>), maps port
        <code>3110:3110</code>, injects <code>PORT</code>/<code>ADMIN_HOST</code>/<code>CUSTOMER_HOST</code> as
        environment variables (dev hostnames: <code>devesign-admin.ihostmyform.com</code> /
        <code>devesign.ihostmyform.com</code>), sets <code>restart: unless-stopped</code>, and mirrors the Dockerfile's
        <code>/health</code> healthcheck. No volumes are mounted — the built assets are baked into the image at build
        time.
    </p>

    <h3>24.6 Server Configuration</h3>
    <p>See §7.4 for the full environment-variable table. No Nginx/Apache is used anywhere in the pipeline — Node/Express
        is the entire serving layer.</p>
    <p class="na">Any deployment target beyond this Docker/Express setup (e.g. Kubernetes manifests, CI/CD pipeline
        definitions, cloud provider configuration) is not available in the provided source code.</p>

    <h2 id="s25">25. Security</h2>

    <h3>25.1 Implemented Mechanisms</h3>
    <table>
        <tr>
            <th>Mechanism</th>
            <th>Implementation</th>
        </tr>
        <tr>
            <td>Authentication</td>
            <td>Bearer-token session, stored in <code>sessionStorage</code>, attached by <code>JwtInterceptor</code> to
                every request (see §5)</td>
        </tr>
        <tr>
            <td>Authorization</td>
            <td>Single-role client-side gate (<code>role === 'admin'</code> / <code>'customer'</code>) via
                <code>AuthGuard</code>; no granular permission model in the frontend
            </td>
        </tr>
        <tr>
            <td>Route guards</td>
            <td>See §17 — one guard per app is applied at the top authenticated route only; Customer's
                <code>eStampActiveGuard</code> is unwired (a gap, not a control)
            </td>
        </tr>
        <tr>
            <td>HTTP interceptors</td>
            <td>Centralized token attachment and error normalization (§16)</td>
        </tr>
        <tr>
            <td>Input validation</td>
            <td>Extensive client-side form/field validation (§15) — client-side only; server-side enforcement cannot be
                confirmed from this frontend codebase</td>
        </tr>
        <tr>
            <td>File validation</td>
            <td>MIME type, extension, and size checks before every upload (§20.1)</td>
        </tr>
        <tr>
            <td>Security headers</td>
            <td><code>helmet()</code> applied in <code>server.js</code>, plus <code>compression()</code> and
                <code>x-powered-by</code> disabled
            </td>
        </tr>
        <tr>
            <td>Non-root container</td>
            <td>The Docker runtime image drops to a dedicated non-root <code>appuser</code></td>
        </tr>
    </table>

    <h3>25.2 Confirmed Gaps / Considerations (code-derived, not speculative)</h3>
    <ul class="gap"
        style="list-style-position: inside; padding: 8px 12px; background:#FCE4E4; border-left:4px solid #C00000; margin:10pt 0;">
        <li><strong>Session token in <code>sessionStorage</code>:</strong> readable by any script executing in the page
            context — an XSS vulnerability anywhere in the app (or a third-party script) could exfiltrate the active
            session token. No <code>HttpOnly</code> cookie-based session is used.</li>
        <li><strong>Content-Security-Policy explicitly disabled:</strong> <code>server.js</code> configures
            <code>helmet(&#123; contentSecurityPolicy: false, crossOriginEmbedderPolicy: false &#125;)</code>, with an
            in-code
            comment acknowledging this should be "audited and tightened per-app later." This removes a key XSS/injection
            mitigation layer at the HTTP-response level.
        </li>
        <li><strong>External PDF.js worker from a public CDN</strong> (<code>cdnjs.cloudflare.com</code>) rather than a
            bundled/self-hosted asset — an availability and supply-chain dependency on a third party for core signing
            functionality.</li>
        <li><strong>Client-generated transaction IDs</strong> for plan/order payments
            (<code>crypto.getRandomValues</code>-based, not from a payment gateway) — confirms no real payment-gateway
            integration exists in the frontend; whatever authoritative payment verification exists must live entirely
            server-side.</li>
        <li><strong>No route-level guard for the E-Stamp module</strong> in the Customer app
            (<code>eStampActiveGuard</code> unwired) — direct URL navigation bypasses the UI-level activation check
            (backend authorization, if present, is the only real control here).</li>
        <li><strong>401 handling inconsistency:</strong> Admin force-clears the session on 401; Customer only shows a
            toast and leaves the (now-invalid) session in place — a user could continue interacting with a UI that will
            keep failing silently until they manually log out.</li>
        <li><strong>Both apps' "production" environment files still point at the dev API host</strong> (§7.1) — a real
            production cutover requires this to be corrected, or it must be overridden by a deployment step not present
            in this repository.</li>
        <li>No CSRF-token handling was found in the frontend (consistent with a pure bearer-token API, where CSRF is
            typically not applicable — noted for completeness).</li>
        <li><strong>Maker/Checker app — no centralized 401 handling:</strong> unlike Admin/Customer, its
            <code>errorInterceptor</code> is a bare pass-through with no logout/redirect/toast logic at all — an expired
            session simply produces per-call failures that individual components may or may not surface.
        </li>
        <li><strong>Maker/Checker app — <code>guestGuard</code> is a no-op</strong> (always returns <code>true</code>) —
            an already-authenticated user can still reach the login/register/forgot-password pages, and there is no
            "redirect away from auth pages if already logged in" behavior.</li>
        <li><strong>Maker/Checker app — role separation is route-level only, not action-level:</strong> the shared
            <code>BulkOrderDetailComponent</code> hardcodes <code>isMaker = true</code>, and Checker's settings writes
            are misrouted to Maker endpoints (§5.3) — these are correctness bugs more than deliberate security holes,
            but they mean the app cannot currently be relied on to keep Maker and Checker actions separated.
        </li>
        <li><strong>Maker/Checker app — documents sent to a third-party OCR/text-extraction API:</strong> the
            <code>pdf-text-extractor</code> tool uploads PDF content to an external AVSS-operated endpoint
            (<code>extract-api.avsstech.in</code>) outside the app's normal backend — worth confirming this is an
            approved data flow for any sensitive stamp/order documents, even though the tool is currently hidden from
            the sidebar.
        </li>
    </ul>

    <h2 id="s26">26. Business Rules</h2>
    <table>
        <tr>
            <th>Rule</th>
            <th>Where implemented</th>
        </tr>
        <tr>
            <td>Admin login is rejected client-side unless the returned user's role is exactly <code>'admin'</code></td>
            <td><code>Admin/.../login.component.ts</code></td>
        </tr>
        <tr>
            <td>Customer login is rejected client-side unless the returned user's role is exactly
                <code>'customer'</code>
            </td>
            <td><code>Customer/.../login.component.ts</code></td>
        </tr>
        <tr>
            <td>New maker users start in <code>PENDING</code> status and must be explicitly approved (or rejected with a
                mandatory reason) by an Admin before use</td>
            <td><code>Admin/.../user.component.ts</code>, <code>UserProfileService</code></td>
        </tr>
        <tr>
            <td>A maker can only be assigned to a client whose <code>estampService</code> status is
                <code>REQUESTED</code>
            </td>
            <td><code>Admin/.../estamp-customer.component.ts</code></td>
        </tr>
        <tr>
            <td>Bulk e-stamp order line items must be unique per State+Denomination combination — duplicates must be
                merged via Quantity</td>
            <td><code>Customer/.../upload-stamp-excel.component.ts</code></td>
        </tr>
        <tr>
            <td>A bulk order's invoice can only be generated while status is <code>ORDER_CREATED</code>; payment can
                only be verified/rejected while status is <code>PAYMENT_SUBMITTED</code></td>
            <td><code>Admin/.../client-order[-detail].component.ts</code></td>
        </tr>
        <tr>
            <td>Enterprise-tier subscription orders require manual entitlement entry (amount/users/documents/end date)
                at completion time; other plans complete with a bare status flip</td>
            <td><code>Admin/.../orders.component.ts</code></td>
        </tr>
        <tr>
            <td>Plan-purchase orders can only be Completed or Cancelled while in <code>Pending</code> status</td>
            <td><code>Admin/.../orders.component.ts</code></td>
        </tr>
        <tr>
            <td>Every signer in an e-sign document must have at least one placed signature position before the document
                can be sent</td>
            <td><code>Customer/.../send-document.component.ts</code>, <code>add-template.component.ts</code> (both apps)
            </td>
        </tr>
        <tr>
            <td>A signer can only continue to the external signing page once the backend has produced their
                <code>redirect_esign_url</code> — enforces sequential-signer completion in the UI
            </td>
            <td><code>Customer/.../my-document.component.ts</code></td>
        </tr>
        <tr>
            <td>Payment-proof images are capped at 2MB, JPG/PNG only; source documents/templates capped at 10MB, PDF
                only; bulk-order Excel capped at 1MB</td>
            <td>Respective upload components (both apps) — see §20.1</td>
        </tr>
        <tr>
            <td>A customer's E-Stamp module is inaccessible (UI-blocked) unless their <code>estampService</code> status
                is <code>ACTIVE</code></td>
            <td><code>Customer/.../sidebar.component.ts</code></td>
        </tr>
        <tr>
            <td>Only the platform's active Admin session (role check) may activate/deactivate tenants, vendors, or
                plans; only a valid Customer session may create orders/documents</td>
            <td><code>AuthGuard</code> in both apps</td>
        </tr>
        <tr>
            <td>A scanned inventory item can only be Approved, Defaced, or Deleted by a Maker while its status is
                <code>NEW</code>
            </td>
            <td>Maker/Checker: <code>features/maker/inventory/inventory-detail/</code></td>
        </tr>
        <tr>
            <td>A bulk order can only be allocated/fulfilled/rejected by a Maker — no Checker-side approval action is
                currently reachable, despite the status vocabulary implying one is intended</td>
            <td>Maker/Checker: <code>shared/components/client-order/</code> (§12.3)</td>
        </tr>
        <tr>
            <td>Registering in the Maker/Checker app requires selecting exactly one role (<code>MAKER</code> or
                <code>CHECKER</code>), which then permanently gates which top-level route tree the account can access
            </td>
            <td>Maker/Checker: <code>features/auth/register/</code>, <code>core/guards/auth.guard.ts</code></td>
        </tr>
    </table>

    <h2 id="s27">27. Dependencies</h2>

    <h3>27.1 Admin App (<code>Admin/package.json</code>)</h3>
    <table>
        <tr>
            <th>Category</th>
            <th>Packages</th>
        </tr>
        <tr>
            <td>Angular framework</td>
            <td><code>&#64;angular/&#123;animations,cdk,common,compiler,core,forms,google-maps,localize,platform-browser,platform-browser-dynamic,router&#125;</code>
                ^17.0.6, <code>&#64;angular/cdk</code> ^17.0.3</td>
        </tr>
        <tr>
            <td>UI/CSS</td>
            <td><code>bootstrap</code> 5.3.2, <code>&#64;ng-bootstrap/ng-bootstrap</code> ^16.0.0,
                <code>primeng</code>/<code>primeicons</code>, <code>ngx-ui-switch</code>,
                <code>&#64;popperjs/core</code>,
                <code>simplebar-angular</code>
            </td>
        </tr>
        <tr>
            <td>Data/tables/forms</td>
            <td><code>&#64;ng-select/ng-select</code>, <code>ngx-mask</code>, <code>ngx-pipes</code>,
                <code>ngx-color-picker</code>, <code>ngx-slider-v2</code>, <code>angular-ng-stepper</code>,
                <code>bs-custom-file-input</code>, <code>ngx-dropzone-wrapper</code>, <code>ngx-drag-drop</code>
            </td>
        </tr>
        <tr>
            <td>Charts/maps</td>
            <td><code>apexcharts</code>/<code>ng-apexcharts</code>, <code>chart.js</code>/<code>ng2-charts</code>,
                <code>echarts</code>/<code>ngx-echarts</code>,
                <code>&#64;asymmetrik/ngx-leaflet</code>/<code>leaflet</code>, <code>&#64;fullcalendar/*</code>
            </td>
        </tr>
        <tr>
            <td>PDF/document</td>
            <td><code>pdf-lib</code>, <code>pdfjs-dist</code>, <code>&#64;ckeditor/ckeditor5-*</code></td>
        </tr>
        <tr>
            <td>Auth/backend</td>
            <td><code>firebase</code> (dormant), <code>ngx-cookie-service</code></td>
        </tr>
        <tr>
            <td>AVSS internal</td>
            <td><code>&#64;avss-tech/logger</code> — access-log SDK</td>
        </tr>
        <tr>
            <td>i18n</td>
            <td><code>&#64;ngx-translate/core</code>, <code>&#64;ngx-translate/http-loader</code></td>
        </tr>
        <tr>
            <td>Utility</td>
            <td><code>rxjs</code>, <code>sweetalert2</code>, <code>ajv</code>/<code>ajv-errors</code>,
                <code>ng-click-outside</code>, <code>metismenujs</code>, <code>tslib</code>, <code>zone.js</code>
            </td>
        </tr>
        <tr>
            <td>Testing</td>
            <td><code>jasmine-core</code>, <code>jasmine-spec-reporter</code>, <code>karma</code> + plugins,
                <code>protractor</code>, <code>&#64;types/jasmine</code>
            </td>
        </tr>
        <tr>
            <td>Dev/build</td>
            <td><code>&#64;angular/cli</code>/<code>&#64;angular-devkit/build-angular</code> ^17,
                <code>&#64;angular-eslint/*</code>, <code>&#64;typescript-eslint/*</code>, <code>eslint</code>,
                <code>codelyzer</code>, <code>tslint</code>, <code>typescript</code> ~5.2.2, <code>ts-node</code>
            </td>
        </tr>
    </table>

    <h3>27.2 Customer App (<code>Customer/package.json</code>)</h3>
    <table>
        <tr>
            <th>Category</th>
            <th>Packages</th>
        </tr>
        <tr>
            <td>Angular framework</td>
            <td><code>&#64;angular/&#123;animations,cdk,common,compiler,core,forms,localize,platform-browser,platform-browser-dynamic,router&#125;</code>
                ^19.2.25</td>
        </tr>
        <tr>
            <td>UI/CSS</td>
            <td><code>bootstrap</code> 5.3.2, <code>&#64;ng-bootstrap/ng-bootstrap</code> ^18.0.0,
                <code>primeng</code>/<code>primeicons</code>, <code>tailwindcss</code> (dev, minimal usage),
                <code>ngx-ui-switch</code>, <code>swiper</code>
            </td>
        </tr>
        <tr>
            <td>Data/forms</td>
            <td><code>&#64;ng-select/ng-select</code> ^14.9.0, <code>ngx-mask</code>, <code>ngx-pipes</code>,
                <code>ngx-image-cropper</code>, <code>bs-custom-file-input</code>
            </td>
        </tr>
        <tr>
            <td>Charts/maps</td>
            <td><code>apexcharts</code>/<code>ng-apexcharts</code>,
                <code>&#64;asymmetrik/ngx-leaflet</code>/<code>leaflet</code>,
                <code>&#64;fullcalendar/&#123;core,daygrid,list,timegrid&#125;</code>
            </td>
        </tr>
        <tr>
            <td>PDF/document/spreadsheet</td>
            <td><code>pdf-lib</code>, <code>pdfjs-dist</code>, <code>html2pdf.js</code>, <code>xlsx</code>,
                <code>&#64;ckeditor/ckeditor5-build-classic</code>
            </td>
        </tr>
        <tr>
            <td>Auth/backend</td>
            <td><code>firebase</code> (dormant), <code>ngx-cookie-service</code> ^19.1.2</td>
        </tr>
        <tr>
            <td>i18n</td>
            <td><code>&#64;ngx-translate/core</code>, <code>&#64;ngx-translate/http-loader</code></td>
        </tr>
        <tr>
            <td>Utility</td>
            <td><code>rxjs</code>, <code>sweetalert2</code>, <code>ng-click-outside</code>, <code>metismenujs</code>,
                <code>tslib</code>, <code>zone.js</code>
            </td>
        </tr>
        <tr>
            <td>Testing</td>
            <td>same Karma/Jasmine/Protractor stack as Admin</td>
        </tr>
        <tr>
            <td>Dev/build</td>
            <td><code>&#64;angular/cli</code>/<code>&#64;angular-devkit/build-angular</code> ^19.2,
                <code>&#64;angular-eslint/*</code> ^19, <code>&#64;typescript-eslint/*</code> ^8,
                <code>autoprefixer</code>/<code>postcss</code>/<code>tailwindcss</code>,
                <code>codelyzer</code>/<code>tslint</code> (legacy, coexists with ESLint), <code>typescript</code>
                ~5.7.3
            </td>
        </tr>
    </table>

    <h3>27.3 Root (Express host, <code>package.json</code>)</h3>
    <p><code>express</code> ^4.19.2, <code>helmet</code> ^7.1.0, <code>compression</code> ^1.7.4 — no devDependencies;
        this is a production-only static host, not an Angular workspace.</p>

    <h2 id="s28">28. Troubleshooting</h2>
    <table>
        <tr>
            <th>Issue</th>
            <th>Cause</th>
            <th>Solution</th>
        </tr>
        <tr>
            <td><code>npm install</code> fails with peer-dependency conflict</td>
            <td><code>&#64;asymmetrik/ngx-leaflet</code> (Customer) requires <code>&#64;angular/common&#64;18</code>,
                but the app
                runs Angular 19.2</td>
            <td>Install with <code>npm ci --legacy-peer-deps</code> (as the Dockerfile does)</td>
        </tr>
        <tr>
            <td>Both apps' dev servers conflict on startup</td>
            <td>Both <code>angular.json</code> serve configs default to port 4200</td>
            <td>Run one app at a time locally, or start the second with <code>ng serve --port 4201</code></td>
        </tr>
        <tr>
            <td>Production build still calls the dev API</td>
            <td><code>environment.prod.ts</code> in both apps currently contains the same dev <code>apiUrl</code> as
                <code>environment.ts</code>
            </td>
            <td>Update <code>environment.prod.ts</code> with the real production API URL before building for release
            </td>
        </tr>
        <tr>
            <td>Direct navigation/refresh on an e-stamp order-detail page shows an empty screen</td>
            <td><code>ClientOrderDetailComponent</code> (Admin) reads only <code>history.state.order</code>, with no
                fallback API fetch by route <code>:id</code></td>
            <td>Navigate to the detail page only via the list screen's "view" action; or (development fix) add a
                <code>getBulkOrderDetail(id)</code> fallback fetch on direct load
            </td>
        </tr>
        <tr>
            <td>Logging out from the horizontal-layout topbar in the Admin app doesn't actually end the session</td>
            <td><code>HorizontaltopbarComponent.logout()</code> calls the legacy
                <code>AuthfakeauthenticationService.logout()</code> (clears <code>localStorage</code>) instead of the
                real <code>AuthenticationService.logout()</code> (clears <code>sessionStorage</code>) in the default
                (<code>'local'</code>) auth mode
            </td>
            <td>Use the vertical-layout topbar's logout, or fix the horizontal topbar to call
                <code>AuthenticationService.logout()</code>
            </td>
        </tr>
        <tr>
            <td>Navigating straight to a Customer <code>/customer/estamp/...</code> URL when e-stamp isn't active
                doesn't redirect</td>
            <td><code>eStampActiveGuard</code> is defined but never attached to the <code>estamp</code> route in
                <code>pages-routing.module.ts</code>
            </td>
            <td>Add <code>canMatch: [eStampActiveGuard]</code> to the <code>estamp</code> route entry</td>
        </tr>
        <tr>
            <td>"Should create" test suite passes but real regressions still ship</td>
            <td>All specs in both apps are unmodified Angular-CLI boilerplate with no real assertions (§22)</td>
            <td>Author real unit tests for guards, interceptors, validators, and service HTTP calls (using
                <code>HttpTestingController</code>) before relying on CI test results as a safety net
            </td>
        </tr>
        <tr>
            <td>Pagination component (<code>app-pagination</code>) changes have no visible effect</td>
            <td>The component is exported but not referenced in any template — all lists use PrimeNG's built-in
                paginator</td>
            <td>Either remove the dead component or intentionally adopt it in a screen if a non-PrimeNG pager is
                genuinely needed</td>
        </tr>
        <tr>
            <td>PDF signing tool fails to render pages in an offline/restricted network environment</td>
            <td><code>pdfjs-dist</code>'s worker script is loaded from a public CDN (<code>cdnjs.cloudflare.com</code>)
                rather than bundled locally</td>
            <td>Self-host the PDF.js worker file and update the <code>workerSrc</code> configuration in
                <code>PdfSignerComponent</code>/<code>ViewPdfComponent</code>
            </td>
        </tr>
        <tr>
            <td>Docker build fails to locate <code>index.html</code></td>
            <td>Angular CLI build output layout varies (<code>dist/nazox/index.html</code> vs
                <code>dist/nazox/browser/index.html</code>) depending on builder/version
            </td>
            <td>The Dockerfile and <code>server.js</code> already search both layouts (max depth 2) — verify the actual
                `ng build` output path matches one of these two patterns if a custom builder is introduced</td>
        </tr>
    </table>

    <h2 id="s29">29. Coding Standards</h2>

    <h3>29.1 Existing Practices (as observed)</h3>
    <ul>
        <li><strong>Module organization:</strong> consistent feature-folder pattern
            (<code>pages/&lt;feature&gt;/&lt;feature&gt;.component.ts</code> + its own routing/NgModule) repeated across
            both apps.</li>
        <li><strong>Service naming:</strong> one <code>&lt;Domain&gt;Service</code> class per backend resource area, all
            <code>providedIn:'root'</code>, all injected via constructor.
        </li>
        <li><strong>Component structure:</strong> standard Angular decorator components (non-standalone) with
            template/style files split out (no inline templates observed in feature components).</li>
        <li><strong>Reactive Forms</strong> used consistently for all data-entry screens, with `FormBuilder`/custom
            validator functions colocated in the component file.</li>
        <li><strong>List/table pattern:</strong> PrimeNG <code>p-table</code> with server-side lazy loading is the
            uniform convention for every paginated list in both apps, paired with the shared
            <code>FilterComponent</code>.
        </li>
        <li><strong>Confirmation-before-destructive-action</strong> pattern via the shared
            <code>ConfirmModalService</code>, applied to most (not all — see §30) delete/approve/reject/status-change
            actions.
        </li>
    </ul>

    <h3>29.2 Practices That Diverge from Convention (observed, not necessarily wrong, but inconsistent)</h3>
    <ul>
        <li><strong>TypeScript strictness:</strong> <code>strict: true</code> is not enabled in either app's
            <code>tsconfig.json</code>; <code>any</code> typing is pervasive across services and components rather than
            dedicated interfaces/DTOs.
        </li>
        <li><strong>Model centralization:</strong> <code>core/models/</code> holds only the <code>User</code> class in
            both apps — nearly every other domain interface (<code>Customer</code>, <code>Plan</code>,
            <code>Vendor</code>, <code>Signer</code>, etc.) is declared inline inside its owning service file rather
            than in a shared models folder.
        </li>
        <li><strong>Two overlapping lint toolchains</strong> (ESLint via <code>&#64;angular-eslint</code>, and the
            deprecated TSLint) are configured simultaneously in both apps.</li>
        <li><strong>Duplicated logic across components:</strong> status-badge-class mapping functions
            (<code>getStatusClass</code>) are copy-pasted near-verbatim across multiple e-stamp components in both apps
            rather than factored into a shared pipe/utility.</li>
    </ul>
    <p class="rec"><strong>Recommended improvement</strong> (not existing practice): enable incremental TypeScript
        strictness (starting with <code>strictNullChecks</code>), consolidate domain interfaces into
        <code>core/models/</code>, retire TSLint in favor of ESLint only, and extract repeated
        status-badge/date-formatting helpers into shared pipes.
    </p>

    <h2 id="s30">30. Known Issues and Technical Debt</h2>
    <p>The following are <strong>confirmed</strong> from source-code analysis (not speculative):</p>
    <table>
        <tr>
            <th>#</th>
            <th>Issue</th>
            <th>App</th>
            <th>Location</th>
        </tr>
        <tr>
            <td>1</td>
            <td>Both apps' <code>environment.prod.ts</code> point at the dev API host and retain
                <code>production: false</code>
            </td>
            <td>Both</td>
            <td><code>src/environments/environment.prod.ts</code></td>
        </tr>
        <tr>
            <td>2</td>
            <td>Redirect path mismatch: guards/logout navigate to <code>/account/login</code> but the routed path is
                <code>/account/auth/login</code>
            </td>
            <td>Both</td>
            <td><code>AuthGuard</code>, topbar logout handlers, <code>PasswordresetComponent</code></td>
        </tr>
        <tr>
            <td>3</td>
            <td>Horizontal-layout logout clears the wrong storage (<code>localStorage</code> via the fake auth service)
                instead of the real session</td>
            <td>Admin</td>
            <td><code>HorizontaltopbarComponent.logout()</code></td>
        </tr>
        <tr>
            <td>4</td>
            <td><code>eStampActiveGuard</code> is defined and imported but never attached to a route</td>
            <td>Customer</td>
            <td><code>pages/pages-routing.module.ts</code></td>
        </tr>
        <tr>
            <td>5</td>
            <td><code>ClientOrderDetailComponent</code> has no independent API fetch by route id — relies solely on
                router navigation <code>state</code></td>
            <td>Admin</td>
            <td><code>pages/e-stamp/client-order/client-order-detail/</code></td>
        </tr>
        <tr>
            <td>6</td>
            <td><code>PaginationComponent</code> is fully implemented and exported but never referenced in any template
                (dead code)</td>
            <td>Admin</td>
            <td><code>shared/components/pagination/</code></td>
        </tr>
        <tr>
            <td>7</td>
            <td><code>StatComponent</code>'s multi-item template loop doesn't bind per-item fields — only the
                component's singular Inputs</td>
            <td>Admin</td>
            <td><code>shared/widget/stat/stat.component.html</code></td>
        </tr>
        <tr>
            <td>8</td>
            <td><code>AuditTrailService</code> returns hardcoded mock data (<code>of(this.data)</code>) instead of a
                live API call; its <code>html2pdf.js</code>-based PDF export call site is commented out</td>
            <td>Customer</td>
            <td><code>core/services/audit-trail.service.ts</code></td>
        </tr>
        <tr>
            <td>9</td>
            <td><code>frontend/status-success</code> fetches real document-status data via API but never binds it into
                the view (only <code>console.log</code>s it)</td>
            <td>Customer</td>
            <td><code>frontend/status-success/status-success.component.ts</code></td>
        </tr>
        <tr>
            <td>10</td>
            <td><code>shared/components/status-page</code> has hardcoded transaction ID/timestamp text in its template,
                unrelated to any real transaction</td>
            <td>Customer</td>
            <td><code>shared/components/status-page/status-page.component.html</code></td>
        </tr>
        <tr>
            <td>11</td>
            <td>Client-side pseudo-random transaction IDs are generated and submitted for plan-order "payments," with no
                real payment gateway integrated in the frontend</td>
            <td>Both</td>
            <td><code>generateTransactionId()</code> in Admin's <code>DetailComponent</code> and Customer's
                <code>BillingComponent</code>
            </td>
        </tr>
        <tr>
            <td>12</td>
            <td><code>BillingService.downloadInvoice()</code> is stubbed
                (<code>of(&#123;success:true,...&#125;)</code>); the real
                HTTP call is commented out</td>
            <td>Customer</td>
            <td><code>core/services/billing.service.ts</code></td>
        </tr>
        <tr>
            <td>13</td>
            <td>Two parallel auth services exist per app with overlapping/misleading names
                (<code>AuthenticationService</code> vs. <code>AuthfakeauthenticationService</code>) — the "fake" one is
                the actual production auth service in Customer</td>
            <td>Both</td>
            <td><code>core/services/auth.service.ts</code>, <code>authfake.service.ts</code></td>
        </tr>
        <tr>
            <td>14</td>
            <td>Several service methods are defined but have no call site anywhere in the app (e.g.
                <code>OrderService.getOrderStatics/getCustomerOrders</code>,
                <code>TemplateService.useTemplate/getAllTemplatesById</code>,
                <code>DocumentService.signDocument/documentStatusByTxn</code> partially,
                <code>S3StorageService.disconnectS3Bucket</code>)
            </td>
            <td>Both</td>
            <td>Various <code>core/services/*.ts</code></td>
        </tr>
        <tr>
            <td>15</td>
            <td>Admin's <code>BulkOrdersComponent</code>-equivalent contains a stray/orphaned
                <code>&#64;Component</code>
                decorator with no matching class body (dead/broken code)</td>
            <td>Customer</td>
            <td><code>pages/e-stamp/bulk-orders/bulk-orders.component.ts</code></td>
        </tr>
        <tr>
            <td>16</td>
            <td>Inconsistent use of the shared confirmation-modal pattern — some destructive actions (e.g. Admin's
                Vendor save/health-check, e-mail template delete) skip it entirely or use a native
                <code>confirm()</code>
            </td>
            <td>Admin</td>
            <td><code>pages/vendors/</code>, <code>pages/email-sttings/</code></td>
        </tr>
        <tr>
            <td>17</td>
            <td>No unit test provides real behavioral coverage anywhere in either app (§22)</td>
            <td>Both</td>
            <td>All <code>*.spec.ts</code> files</td>
        </tr>
        <tr>
            <td>18</td>
            <td>Content-Security-Policy is explicitly disabled in the production server config, with an in-code TODO
                acknowledging it</td>
            <td>Infra</td>
            <td><code>server.js</code></td>
        </tr>
        <tr>
            <td>19</td>
            <td>The project/output directory name (<code>nazox</code>) does not match the product branding anywhere in
                build config, a leftover from the original admin theme template</td>
            <td>Both</td>
            <td><code>angular.json</code>, <code>package.json</code></td>
        </tr>
        <tr>
            <td>20</td>
            <td>Two overlapping/duplicate lint toolchains (ESLint + deprecated TSLint) ship simultaneously</td>
            <td>Both</td>
            <td><code>tslint.json</code> + <code>.eslintrc</code> config in both apps</td>
        </tr>
        <tr>
            <td>21</td>
            <td><strong>Checker role cannot open an order-detail screen or take any approve/reject/remark
                    action</strong> — the shared order-list's "view" action always routes to a Maker-only page, no
                <code>/checker/order/:id</code> route exists, and the backend-facing
                <code>updateStatus()</code>/<code>checkerRemark()</code> methods have no call site
            </td>
            <td>Maker/Checker</td>
            <td><code>shared/components/client-order/</code>, <code>core/services/order.service.ts</code></td>
        </tr>
        <tr>
            <td>22</td>
            <td><code>BulkOrderDetailComponent</code> hardcodes <code>isMaker = true</code> in <code>ngOnInit()</code>,
                independent of route or actual user role</td>
            <td>Maker/Checker</td>
            <td><code>shared/components/client-order/bulk-order-detail/</code></td>
        </tr>
        <tr>
            <td>23</td>
            <td>Checker's profile-update and change-password actions are misrouted to Maker's backend endpoints (only
                the profile <em>read</em> is role-aware)</td>
            <td>Maker/Checker</td>
            <td><code>core/services/settings.service.ts</code></td>
        </tr>
        <tr>
            <td>24</td>
            <td><code>guestGuard</code> is a hardcoded no-op (<code>return true;</code>) — an authenticated user is
                never redirected away from the login/register/forgot-password pages</td>
            <td>Maker/Checker</td>
            <td><code>core/guards/guest.guard.ts</code></td>
        </tr>
        <tr>
            <td>25</td>
            <td><code>errorInterceptor</code> performs no centralized error handling at all (bare pass-through) — unlike
                Admin/Customer, there is no app-wide toast or 401 behavior</td>
            <td>Maker/Checker</td>
            <td><code>core/interceptors/error.interceptor.ts</code></td>
        </tr>
        <tr>
            <td>26</td>
            <td>Login always navigates to <code>/maker/dashboard</code> regardless of the returned user's actual role,
                relying on <code>authGuard</code> to bounce Checkers onward afterward</td>
            <td>Maker/Checker</td>
            <td><code>features/auth/login/login.component.ts</code></td>
        </tr>
        <tr>
            <td>27</td>
            <td><code>FooterComponent</code> is only ever shown when the current URL is an <code>/auth/*</code> path,
                but it's mounted inside <code>MainLayoutComponent</code> which is exclusively used for
                <code>/maker</code>/<code>/checker</code> routes — the condition can never be true, so the footer never
                renders in practice
            </td>
            <td>Maker/Checker</td>
            <td><code>layout/main-layout/</code></td>
        </tr>
        <tr>
            <td>28</td>
            <td>Several service methods have no call site (e.g. <code>ReportsService</code> and
                <code>VendorService</code> are empty stubs; <code>OrderHistoryService</code>'s auto-print call is
                commented out, so "print" currently only opens a pre-filled dialog without triggering the browser print
                action)
            </td>
            <td>Maker/Checker</td>
            <td><code>core/services/*.ts</code></td>
        </tr>
        <tr>
            <td>29</td>
            <td>Admin/Customer call a different backend host (<code>devesignapi.ihostmyform.com</code>) than the
                Maker/Checker app (<code>devestampapi.ihostmyform.com</code>) for what is functionally the same order
                lifecycle — architecture/ownership of this split is not visible from the frontend</td>
            <td>Cross-app</td>
            <td><code>environment.ts</code> in each app</td>
        </tr>
    </table>

    <h2 id="s31">31. Future Improvements</h2>
    <p class="rec">The following are <strong>suggestions</strong>, not existing functionality:</p>
    <ol>
        <li><strong>Security:</strong> move the session token out of <code>sessionStorage</code> into an
            <code>HttpOnly</code> cookie (if the backend can support it) to reduce XSS exposure; re-enable and tune a
            Content-Security-Policy in <code>server.js</code>; self-host the PDF.js worker instead of depending on a
            public CDN.
        </li>
        <li><strong>Testing:</strong> replace boilerplate specs with real unit tests — guard authorization logic,
            interceptor header/error/redirect behavior, form validators (especially the e-stamp Excel and
            signature-position validators), and HTTP service methods via <code>HttpTestingController</code>; consider
            replacing Protractor (EOL) with a modern E2E tool (e.g. Playwright/Cypress).</li>
        <li><strong>Consistency fixes:</strong> correct the <code>/account/login</code> vs
            <code>/account/auth/login</code> redirect mismatch; fix the horizontal-layout logout in Admin; wire
            <code>eStampActiveGuard</code> onto the Customer <code>estamp</code> route; give
            <code>ClientOrderDetailComponent</code> a route-param fallback fetch.
        </li>
        <li><strong>Refactoring:</strong> consolidate the duplicated <code>getStatusClass()</code> badge-mapping logic
            into a shared pipe/directive per status domain; centralize scattered inline interfaces into
            <code>core/models/</code>; remove dead code (unused service methods, the orphaned
            <code>&#64;Component</code>
            decorator, the unused <code>PaginationComponent</code> or adopt it consistently).
        </li>
        <li><strong>Reusability:</strong> extract a small generic paginated-HTTP-service base class to remove the
            repeated <code>HttpParams</code>-building/<code>totalCount</code>-mapping boilerplate present in nearly
            every service.</li>
        <li><strong>Maintainability:</strong> rename the underlying Angular CLI project (<code>nazox</code> → a
            product-aligned name) in both <code>angular.json</code>/<code>package.json</code>; drop the legacy TSLint
            config in favor of ESLint alone; enable incremental TypeScript strict-mode flags.</li>
        <li><strong>Architecture:</strong> if a maker/checker web application exists or is planned outside this
            repository, formalize the handoff contract (API shape, status vocabulary) so the "checker" concept currently
            only present as a CSS class name in this codebase becomes an intentional integration point rather than dead
            styling.</li>
        <li><strong>Feature completion:</strong> wire the live audit-trail endpoint into
            <code>AuditTrailComponent</code> (replacing the mock data), complete the currently-commented-out PDF export
            of the audit trail, and either implement or remove the disabled per-state maker-approval and
            active/inactive-toggle UI in Admin's <code>UserComponent</code>.
        </li>
        <li><strong>Payments:</strong> integrate a real payment gateway for plan orders/e-stamp payments rather than
            relying on client-generated transaction IDs and manual proof-of-payment upload/verification.</li>
        <li><strong>Complete the Checker workflow:</strong> give the Checker role its own order-detail route, fix the
            shared order-list's "view" action to respect the current role, remove the hardcoded
            <code>isMaker = true</code> in <code>BulkOrderDetailComponent</code>, and wire the existing
            <code>updateStatus()</code>/<code>checkerRemark()</code> service methods into an actual
            approve/reject/remark screen.
        </li>
        <li><strong>Fix the Maker/Checker app's settings routing bug</strong> so Checker profile/password updates hit
            checker-scoped endpoints, not maker's; implement <code>guestGuard</code> properly; add a centralized error
            handler (toast + 401 handling) to its <code>errorInterceptor</code>.</li>
        <li><strong>Reconcile the two backend hosts</strong> (<code>devesignapi</code> vs <code>devestampapi</code>)
            that both operate on the e-stamp order lifecycle, or at minimum document the intended relationship between
            them for future maintainers.</li>
    </ol>

    <h2 id="s32">32. Technical Glossary</h2>
    <table>
        <tr>
            <th>Term</th>
            <th>Meaning in this project</th>
        </tr>
        <tr>
            <td>E-Sign</td>
            <td>Electronic document signing feature area; supports Aadhaar OTP, DSC, and Aadhaar Biometric signing
                methods (selected per recipient, actual authentication happens on an external provider page)</td>
        </tr>
        <tr>
            <td>E-Stamp</td>
            <td>Digital stamp-duty paper ordering/fulfilment feature area; supports single/on-demand and Excel-driven
                bulk orders</td>
        </tr>
        <tr>
            <td>DSC</td>
            <td>Digital Signature Certificate — one of the three selectable e-sign authentication methods; represented
                in the frontend only as a selection value and in mock audit-trail data (certificate
                serial/issuer/validity), with no client-side PKI handling</td>
        </tr>
        <tr>
            <td>BSR ID</td>
            <td>Bulk Stamp Request identifier — the order identifier used throughout the Admin e-stamp order screens
                (<code>bsrId</code> field)</td>
        </tr>
        <tr>
            <td>Maker</td>
            <td>An e-stamp fulfilment-operations user (role <code>MAKER</code> in the separate Maker/Checker app) who
                processes bulk stamp orders — allocates inventory, uploads scanned stamp PDFs, and fulfils or rejects
                orders; separately, Admin approves/rejects and assigns maker <em>user accounts</em> to specific clients
            </td>
        </tr>
        <tr>
            <td>Checker</td>
            <td>A second-role user (role <code>CHECKER</code>) in the Maker/Checker app, intended to perform a
                review/approval step (reflected in the <code>UNDER_CHECKER_REVIEW</code> status and dedicated backend
                endpoints) — currently unable to act on any order through the app's UI (§12.3)</td>
        </tr>
        <tr>
            <td>Scanned inventory item statuses</td>
            <td><code>NEW</code> (just uploaded) → <code>APPROVED</code> / <code>DEFACED</code> / <code>DELETED</code>,
                set by a Maker via the inventory detail screen; distinct from the order-level inventory status
                (<code>AVAILABLE/RESERVED/ALLOCATED/UTILIZED</code>) shown to Admin/Customer</td>
        </tr>
        <tr>
            <td>PDF Text Extractor</td>
            <td>A standalone tool in the Maker/Checker app that extracts text from user-drawn regions of a PDF via an
                external third-party API — not part of the core stamp-upload workflow, currently hidden from navigation
            </td>
        </tr>
        <tr>
            <td>Signer</td>
            <td>A recipient of an e-sign document; not an application user account — identified by name/email/phone and
                authenticated externally via a provider-hosted redirect URL</td>
        </tr>
        <tr>
            <td>Signature position / signature box</td>
            <td>A rectangular region (page, x, y, width, height, in PDF points) placed on a document indicating where a
                given signer must sign; placed via <code>PdfSignerComponent</code></td>
        </tr>
        <tr>
            <td>Template</td>
            <td>A reusable base document (PDF + placeholder signers + pre-placed signature positions) that can be used
                to quickly start a new e-sign document</td>
        </tr>
        <tr>
            <td>Tenant / Customer</td>
            <td>A business account using the platform's e-sign/e-stamp services, managed from the Admin app's Customers
                screen</td>
        </tr>
        <tr>
            <td>Inventory (scanned stamps)</td>
            <td>Individually tracked physical/e-stamp items with a lifecycle
                (<code>AVAILABLE → RESERVED → ALLOCATED → UTILIZED</code>), read-only from both frontends</td>
        </tr>
        <tr>
            <td>Fake backend / <code>fackbackend</code></td>
            <td>The value of <code>environment.defaultauth</code> (sic — typo preserved from source) selecting the real
                session-storage-based auth path over the dormant Firebase path; not related to
                <code>FakeBackendInterceptor</code>'s mock HTTP responses (a separate, unrelated legacy artifact in the
                Admin app)
            </td>
        </tr>
        <tr>
            <td>Nazox</td>
            <td>The original Angular admin-theme template this codebase was built from; still the internal Angular CLI
                project name/output directory in both apps</td>
        </tr>
        <tr>
            <td>Lazy loading</td>
            <td>Angular's <code>loadChildren</code> route mechanism, used for every feature module in both apps to split
                the JS bundle</td>
        </tr>
    </table>

    <h2 id="s33">33. Final Technical Summary</h2>

    <h3>Architecture</h3>
    <p>Three independent Angular applications across two repositories. Admin (v17) and Customer (v19), in
        <code>esign_frontend_angular</code>, are NgModule-based and share a nearly identical structural pattern
        (core/layouts/pages/shared) but no shared code library; both are lazy-loaded at the feature-module level,
        guarded by a single top-level <code>AuthGuard</code>, and served together at runtime by one Express host routed
        by HTTP <code>Host</code> header. The Maker/Checker app (v21), in the separate
        <code>estamp_frontend_angular</code> repository, uses Angular's newer standalone-components model and its own
        independent build/deploy pipeline.
    </p>

    <h3>Technology Stack</h3>
    <p>Admin/Customer: Angular + TypeScript (non-strict) + RxJS, Bootstrap 5 + PrimeNG as the UI foundation,
        <code>pdfjs-dist</code>/<code>pdf-lib</code> for all PDF rendering and signature-placement tooling,
        <code>xlsx</code> for bulk e-stamp order validation. Maker/Checker: Angular + Angular Material (the only one of
        the three apps using it), Chart.js for dashboards, <code>pdfjs-dist</code> for its standalone text-extraction
        tool only. None of the three apps use a dedicated state-management library — state lives in singleton services,
        <code>BehaviorSubject</code>s, and <code>sessionStorage</code> throughout.
    </p>

    <h3>Major Modules</h3>
    <p>E-Sign (document authoring, signature placement, tracking, templates) and E-Stamp order creation live in
        Customer; platform administration (tenants, plans, vendors, settings, notifications) plus E-Stamp order/payment
        oversight and maker approval live in Admin; physical/e-stamp order fulfilment (inventory allocation,
        scanned-stamp upload, approve/deface/delete of inventory items) lives in the Maker/Checker app's Maker role —
        with the Checker role's intended second-level review not currently reachable.</p>

    <h3>Important Workflows</h3>
    <p>E-sign document lifecycle from PDF upload through external provider-hosted signing to completion and audit-trail
        retrieval (Customer app only). E-stamp order lifecycle spans all three apps: creation and payment (Customer) →
        invoice generation and payment verification (Admin) → inventory allocation, scanned-stamp upload, and fulfilment
        or rejection (Maker) — linked end-to-end by a shared <code>bsrId</code> and status vocabulary, despite
        Admin/Customer and Maker/Checker calling two different backend hosts.</p>

    <h3>API Integration</h3>
    <p>Every service in every app follows a consistent relative-path + interceptor-injected-base-URL pattern, uniformly
        paginated (PrimeNG lazy-load events in Admin/Customer, Angular Material table paging in Maker/Checker), with a
        sizeable number of defined-but-unused service methods across all three apps (technical debt, not missing
        functionality).</p>

    <h3>Security</h3>
    <p>Bearer-token session in <code>sessionStorage</code> across all three apps. Admin/Customer centralize token
        attachment and error handling via interceptors; the Maker/Checker app attaches tokens the same way but has no
        centralized error/401 handling at all. Confirmed gaps: disabled CSP, an unwired Customer e-stamp access guard, a
        no-op Maker/Checker guest guard, inconsistent 401 handling across all three apps, role-separation bugs in the
        Maker/Checker app (hardcoded <code>isMaker</code>, misrouted Checker settings writes), and "production"
        environment files that still target development API hosts.</p>

    <h3>Testing</h3>
    <p>A complete Karma/Jasmine scaffold exists in Admin and Customer, but every spec file is unmodified boilerplate —
        there is no real behavioral test coverage in either. The Maker/Checker app is configured with the same
        Karma/Jasmine stack; its test content was not analyzed in this pass.</p>

    <h3>Deployment</h3>
    <p>Admin and Customer: a single multi-stage Docker build compiles both apps and packages them with a minimal Express
        static host (non-root, health-checked, gzip-compressed, cache-header-aware), orchestrated via
        <code>docker-compose up -d --build</code>. The Maker/Checker app has its own, independent Dockerfile and
        docker-compose configuration in its own repository, deployed separately.
    </p>

    <h3>Key Technical Considerations for Ongoing Work</h3>
    <ul>
        <li>Correct the production environment configuration in all three apps before any real production release.</li>
        <li>Complete the Checker approval workflow in the Maker/Checker app — this is the single highest-value
            functional gap found across the platform (§12.3).</li>
        <li>Treat the payment-gateway integration as a backend/external-system concern not yet reflected in any of the
            three frontends.</li>
        <li>Prioritize closing the identified routing/guard/logout inconsistencies across all three apps (§30) and
            building real test coverage before further feature work, since both are low-effort, high-value fixes
            relative to the size of the codebase.</li>
        <li>Confirm with the backend team whether the two API hosts (<code>devesignapi</code> vs
            <code>devestampapi</code>) are intended to remain separate long-term.
        </li>
    </ul>

    <p style="margin-top:36pt;color:#7F7F7F;font-size:9pt;">— End of document. This documentation reflects only
        functionality confirmed present in the analyzed source code of the <code>esign_frontend_angular</code> and
        <code>estamp_frontend_angular</code> repositories as of the stated document date. Where information could not be
        confirmed from the source, it is explicitly marked "Not available in the provided source code."
    </p>

</div>